import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { finalize, map, Observable } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AppSettings } from 'src/app/app.settings';
import { FasteignService } from 'src/app/components/services/api/fasteign/fasteign.service';
import { HafaSambandService } from 'src/app/components/services/api/hafaSamband/hafaSamband.service';
import { NotandiService } from 'src/app/components/services/api/notandi/notandi.service';
import { LsrSeoLinkService } from 'src/app/helpers/canonicalTag';
import { getCookie } from 'src/app/helpers/cookies';
import { epilogExtractApartmentIdentity, epilogExtractHeightNumber } from 'src/app/helpers/epilogDecode';
import { ExternalLinkWatcherService } from 'src/app/helpers/externalLinkWatcherService';
import { hidePreloader } from 'src/app/helpers/hidePreloader';
import { formatPriceString, thousandSeparator } from 'src/app/helpers/thousandSeparator';
import { ValidateEmail } from 'src/app/helpers/validators';
import { AccountsLoginRes } from 'src/app/interfaces/CRUDL/read/AccountsLoginRes';
import { ExposedHafaSambandReq } from 'src/app/interfaces/requests/Exposed/exposedHafaSambandReq';
import { ExposedListPublicKaupskraThjodskraHmsSearchReq } from 'src/app/interfaces/requests/Exposed/exposedListPublicKaupskraThjodskraHmsSearchReq';
import { ExposedThjodskraLeitaFasteignRes } from 'src/app/interfaces/responses/Exposed/exposedThjodskraLeitaFasteignRes';
import { ListFastinnFasteignAdsWithImagesRes } from 'src/app/interfaces/responses/List/listFastinnFasteignAdsWithImagesRes';
import { ListFastinnFasteignImageRes } from 'src/app/interfaces/responses/List/listFastinnFasteignImageRes';
import { ListMblFasteignRes } from 'src/app/interfaces/responses/List/listMblFasteignRes';
import { ListPublicKaupskraThjodskraNearbyFastanumerRes } from 'src/app/interfaces/responses/List/listPublicKaupskraThjodskraNearbyFastanumerRes';
import { ListPublicKaupskraThjodskraRes } from 'src/app/interfaces/responses/List/listPublicKaupskraThjodskraRes';
import { ListTilSoluEignirRes } from 'src/app/interfaces/responses/List/listTilSoluEignirRes';
import { ReadFasteignMetaDataRes } from 'src/app/interfaces/responses/Read/readFasteignMetaDataRes';
import { ReadPublicKaupskraThjodskraRes } from 'src/app/interfaces/responses/Read/readPublicKaupskraThjodskraRes';
import { EmbedVideoService } from 'src/app/services/embed-video.service';
import { LeafletService } from 'src/app/services/leaflet-service.service';
import { environment } from 'src/environments/environment';

declare const Fancybox: any; // Lýsing fyrir Fancybox

@Component({
  selector: 'app-property-sold',
  templateUrl: './property-sold.component.html',
  styleUrls: ['./property-sold.component.scss'],
  providers: [EmbedVideoService]
})
export class PropertySoldComponent implements OnInit {

  //#region Globals

  @ViewChild('verdmatsbeidniForm', { static: false }) verdmatsbeidniForm!: ElementRef<HTMLElement>;

  public _isBrowser: boolean;
  public _isServer: boolean;

  public today: Date = new Date();
  metadata: ReadFasteignMetaDataRes;
  _environment = environment;

  forsiduMyndUrl: string = "";
  public fastnum: string;
  public fasteignSaga: Array<ListPublicKaupskraThjodskraRes> = [];
  public sortedFasteignTotal: Array<ListPublicKaupskraThjodskraRes> = [];
  public fasteignSagaRevertedTotal: Array<ListPublicKaupskraThjodskraRes> = [];
  public _fasteign: ReadPublicKaupskraThjodskraRes;
  public auglysingasaga: Array<ListFastinnFasteignAdsWithImagesRes>;
  public imagesLastAd: Array<ListFastinnFasteignImageRes>;
  public tengdarEignir: Array<ListPublicKaupskraThjodskraRes> = [];
  public verdmat: any;
  public tilSolu: boolean = false;
  public tilSoluFasteignSkraningId = null;

  // Auglýsingar
  public adMobileUrl: SafeResourceUrl;
  public adDesktopUrl: SafeResourceUrl;

  // Loading state og error
  loading = true;
  errorFetchingPropertyInfo = false;

  // Segir til um hvort síðan sé sett inn úr iframe
  pageInject = false;

  // Ef notandi er innskráður.
  userDetails: AccountsLoginRes;

  //#endregion

  //#region Constructor and Init

  @HostListener('window:resize', [])
  onResize(): void {
    this.sendHeightToParent(); // Sendir upplýsingar um hæðina ef skjástærð breytist
  }

  ngOnChanges(): void {
    this.sendHeightToParent(); // Sendir upplýsingar um hæðina ef skjástærð breytist
  }

  constructor(
    private readonly _lsrSeoLink: LsrSeoLinkService,
    private _externalLinkWatcher: ExternalLinkWatcherService,
    private _snackBar: MatSnackBar,
    public _appSettings: AppSettings,
    public _appService: AppService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    public _fasteignService: FasteignService,
    public _hafaSambandService: HafaSambandService,
    public _mediaObserver: MediaObserver,
    public _title: Title,
    public _dialog: MatDialog,
    public _meta: Meta,
    private _sanitizer: DomSanitizer,
    private _notandiService: NotandiService,
    private _leafletService: LeafletService,
    public _fb: UntypedFormBuilder,
    @Inject(PLATFORM_ID) _platformId: Object
  ) {
    this._isBrowser = isPlatformBrowser(_platformId);
    this._isServer = isPlatformServer(_platformId);
    this.loading = true;

    if (this._isBrowser) {
      try {
        this._notandiService.userDetails.subscribe(userDetails => this.userDetails = userDetails);

        // Sendir hæð til parent strax í byrjun.
        this.sendHeightToParent();
      } catch { }
    }

    this.updateOnFasteignClickCurrentPage();
  }

  ngOnInit(): void {
    // Sæki fastanúmer í url
    this.fastnum = this._activatedRoute.snapshot.paramMap.get('id');

    // Ef við erum á mitt.e-fasteignir.is
    if (this._router.url.includes('portal/kaupsagan')) {
      this.pageInject = true;
    }

    // Ef við erum ekki inni í iframe
    if (!this.pageInject) {
      // Þurfum bara að setja meta data ef við erum ekki inni í iframe
      this.setMeta();
    }

    if (this._isBrowser) {
      // Sæki upplýsingar um mblFasteignina ef við erum að birta fyrir mbl.
      this.httpFetch();

      // Sækir upplýsingar fyrir kort
      this.mapInitHttpFetch();

      // Frumstillum auglýsingar
      this.adUrlInit();

      // Frumstillum verðmat
      this.initFormVerdmat();

      // Ef við erum inni í iframe
      if (this.pageInject) {
        // Passa að fjarlægja scroll frá container ef við erum inni í iframe sjáist.
        // Á test síðum fyrir MBL þá hödlum við scroll inni.
        if (!this._router.url.includes('/mbl-test')) {
        }
        // Sendir hæð til parent strax í byrjun.
        this.sendHeightToParent();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this._isBrowser) {

      hidePreloader();

      this.sendHeightToParent();

      this.onResizeChart();

    }
  }

  httpFetch() {
    if (this._isBrowser) {
      // Kanna hvort að eign sé skráð til sölu.
      this._fasteignService.checkTilSoluAllar(this.fastnum).subscribe(res => {
        this.tilSoluFasteignSkraningId = res.fasteignSkraningId;
        this.tilSolu = res.isTilSolu;
      })

      // Sæki upplýsingar um valda fasteign.
      this._fasteignService.readPublicKaupskraThjodskra({ Fastnum: this.fastnum, ForceHmsFetch: true }).subscribe(fasteignRes => {

        if (fasteignRes == null) {
          this.errorFetchingPropertyInfo = true;
          return;
        }

        this._fasteign = fasteignRes;

        // Reiknum verðmat fasteignar.
        this.getVerdmat();

        // Sækjum auglýsingasögu, ef eignin fannst í Fastinn töflum hjá okkur.
        // Birtum heldur aldrei auglýsingasögu ef við erum að birta fyrir vef MBL.
        if (fasteignRes?.fastinnFasteignId) {
          this._fasteignService.listAuglysingasaga(this.fastnum).subscribe(res => {
            this.auglysingasaga = res;
            this.prepareAuglysingasagaChartData();
          });
          this._fasteignService.listAuglysingasagaMyndir(fasteignRes.fastinnFasteignId).subscribe(imagesRes => {
            this.imagesLastAd = imagesRes;
          });
        }

        // Sæki forsíðumynd
        this._fasteignService.kaupskraSaekjaProfilMynd({ Fastnum: fasteignRes.fastnum.toString(), Lat: fasteignRes.latWgs84, Lng: fasteignRes.longWgs84, Postnumer: fasteignRes.postNumer }).subscribe(forsiduMyndUrl => {
          this.forsiduMyndUrl = forsiduMyndUrl;
        });

        this.loading = false;
      });

      // Sæki kaupsamningssögu fyrir fasteignina sem er valin.
      this._fasteignService.listPublicKaupskraThjodskraFastanumerSaga({ Fastnum: this.fastnum }).subscribe(fasteignSagaRes => {
        // Afritum fasteignSagaRes svo við skemmdum ekki upprunalegu gögnin
        this.sortedFasteignTotal = [...fasteignSagaRes].sort((a, b) =>
          new Date(b.thinglystDags).getTime() - new Date(a.thinglystDags).getTime()
        );

        // Vil hafa alla söguna í stækkandi röð
        const sortedFasteignSagaReverted = [...fasteignSagaRes].sort((a, b) =>
          new Date(a.thinglystDags).getTime() - new Date(b.thinglystDags).getTime()
        );

        // Vil eiga stöðuna globalt í stækkandi röð
        this.fasteignSagaRevertedTotal = sortedFasteignSagaReverted;

        // Vil að sagan innihaldi allt nema nýjasta stakið.
        this.fasteignSaga = this.sortedFasteignTotal;

        this.prepareKaupverdLinuritData();
      });

      // Kannar hvort það eigi að framkvæma eitthvað eftir að búið er að hlaða inn gögnum
      this.checkUrlRender();
    }
  }

  public setMeta(): void {
    this._fasteignService.seldMetadata(this.fastnum).subscribe(res => {
      const pageTitle = res.fasteign || 'e-fasteignir | Kaupsagan';
      const pageDescription = res.inngangstexti?.trim() || 'Skoðaðu fasteignir sem hafa verið seldar í gegnum e-fasteignir.';
      const pageImage = res.myndAzureName
        ? res.myndAzureName
        : `${this._environment.rootPublicUrl}/assets/e-fasteignir/images/sold-cover-1.png`;
      const pageUrl = `${this._environment.rootPublicUrl}/kaupsagan/${this.fastnum}`;;

      // Stillir forsíðumyndefni ef það er til staðar
      this.forsiduMyndUrl = pageImage;

      // Uppfærir meta-tög
      this._title.setTitle(pageTitle);
      this._meta.updateTag({ name: 'description', content: pageDescription });
      this._meta.updateTag({ name: 'keywords', content: 'kaupsamningar, fasteignasaga, auglýsingasaga, lán, þinglýstir eigendur, fasteignavottorð, veðbandayfirlit, fasteignir, fasteignaupplýsingar, samanburður fasteigna, nýjustu kaupsamningar, efasteignir, e-fasteignir, fasteign, fasteignir, fasteignasölur, Blue E Solutions ehf.' });
      this._meta.updateTag({ property: 'og:title', content: pageTitle });
      this._meta.updateTag({ property: 'og:url', content: pageUrl });
      this._meta.updateTag({ property: 'og:image', content: pageImage });
      this._meta.updateTag({ property: 'og:description', content: pageDescription });
      this._meta.updateTag({ name: 'twitter:title', content: pageTitle });
      this._meta.updateTag({ name: 'twitter:description', content: pageDescription });
      this._meta.updateTag({ name: 'twitter:image', content: pageImage });
      this._meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this._lsrSeoLink.updateCanonicalTag(pageUrl);
    });
  }

  adUrlInit() {
    const rawAdMobileUrl = 'https://app.pulsmedia.is/snjallbordi/np90fk4giuce6ot/';
    this.adMobileUrl = this._sanitizer.bypassSecurityTrustResourceUrl(rawAdMobileUrl);

    const rawAdDesktopUrl = 'https://app.pulsmedia.is/snjallbordi/shxgbmli04ybedl/';
    this.adDesktopUrl = this._sanitizer.bypassSecurityTrustResourceUrl(rawAdDesktopUrl);
  }

  /**
   * Kannar hvort það eigi að framkvæma eitthvað action miðað við færibreytu úr URL.
   */
  checkUrlRender() {
    if (this._isBrowser) {
      if (window.location.href.includes("verdmatsbeidni")) {
        const interval = setInterval(() => {
          const element = document.getElementById("verdmatsbeidniForm");
          if (element) {
            clearInterval(interval);

            // Bíddu í 5 sek eftir að þetta er ready
            setTimeout(() => {
              element.focus();
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }, 1500);
          }
        }, 500);
      }
    }
  }

  //#endregion

  //#region Línurit

  // --------- Sameiginlegar stillingar fyrir charts

  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = false;
  showXAxisLabel = true;
  showYAxisLabel = false;
  autoScale = true;

  view: [number, number] = [100, 300]; // 0 tryggir að breiddin verður 100%

  yAxisTickFormattingKr = (value: number) => `${value.toLocaleString('de-DE')} m.kr.`;
  xAxisTickFormattingDate = (value: string) => {
    return this.formatDate(new Date(value));
  };

  formatDate(date: Date) {
    return moment(date).format('DD.MM.YYYY');
  }

  onResizeChart() {
    if (this._isBrowser) {
      const interval = setInterval(() => {
        const leftContainer = document.querySelector('.left-container');
        if (leftContainer) {
          const width = leftContainer.scrollWidth || 0;
          this.view = [width / 1.05, 200];
          clearInterval(interval); // Stöðva þegar fundið
        }
      }, 500);
    }
  }

  // --------- Kaupsamningar

  kaupverdLinuritData: any[] = [];
  xAxisKaupverdLabel = 'Kaupdagur';
  yAxisKaupverdLabel = 'Kaupverð';
  selectedKaupsagaIndex: number | null = null;

  customColorsKaupsamningar = [
    { name: 'Kaupverð', value: '#253a85' } // Dökk blár litur fyrir línuna
  ];

  prepareKaupverdLinuritData() {
    this.kaupverdLinuritData = [
      {
        name: 'Kaupverð',
        series: this.fasteignSagaRevertedTotal
          .filter((item) => item.thinglystDags && item.kaupverd) // Henda út röðum með null gildum
          .map((item) => ({
            name: item.thinglystDags as string,
            value: Number(((item.kaupverd) / 1000).toFixed(2)),
          })),
      },
    ];
  }

  onKaupsamningurPointClick(event: any): void {
    // Finna index fyrir samsvarandi auglýsingu út frá dagsetningu
    const index = this.fasteignSaga.findIndex(
      (a) => new Date(a.thinglystDags).toDateString() === new Date(event.name).toDateString()
    );

    if (index !== -1) {
      // Ef sami flipinn er valinn aftur, loka expand (setja null)
      this.selectedKaupsagaIndex = this.selectedKaupsagaIndex === index ? null : index;
    }

    // Ef smellt er á síðasta stakið í fylkinu, scrolla alla leið upp
    if (index === -1) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }

  // --------- Auglýsingasaga

  auglysingasagaLinuritData: any[] = [];
  xAxisAuglysingasagaLabel = 'Dagsetning auglýsingar';
  yAxisAuglysingasagaLabel = 'Auglýst verð';
  selectedAuglysingasagaIndex: number | null = null;

  customColorsAuglysingasaga = [
    { name: 'Auglýst verð', value: '#253a85' } // Dökk blár litur fyrir línuna
  ];

  prepareAuglysingasagaChartData() {
    this.auglysingasagaLinuritData = [
      {
        name: 'Auglýst verð',
        series: this.auglysingasaga
          .filter(item =>
            item.dagsetningSkraningar &&
            !isNaN(Number(item.verd)) &&
            Number(item.verd) > 0 // Sleppa verðgildum sem eru 0
          )
          .map(item => ({
            name: item.dagsetningSkraningar as string, // Breytum í dagsetningarstreng
            value: Number(((item.verd) / 1000000).toFixed(2)), // Tryggjum að þetta sé tala
          }))
          .reverse(), // Snúum röðinni við
      },
    ];
  }

  onAuglysingPointClick(event: any): void {
    // Finna index fyrir samsvarandi auglýsingu út frá dagsetningu
    const index = this.auglysingasaga.findIndex(
      (a) => new Date(a.dagsetningSkraningar).toDateString() === new Date(event.name).toDateString()
    );

    if (index !== -1) {
      // Ef sami flipinn er valinn aftur, loka expand (setja null)
      this.selectedAuglysingasagaIndex = this.selectedAuglysingasagaIndex === index ? null : index;
    }
  }

  // ---------- Fasteign skráð til sölu, auglýsing lýsing

  public isPropertyAuglysingLysingExpanded = false;

  public togglePropertyAuglysingLysingExpanded(): void {
    // Ef verið er að minnka (expanded → collapsed)
    if (this.isPropertyAuglysingLysingExpanded) {
      this.isPropertyAuglysingLysingExpanded = false;

      // Bíð smá áður en scroll er keyrt svo hæðin hafi minnkað
      setTimeout(() => {
        const el = document.getElementById('forsidumynd');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);

    } else {
      // Annars bara opna
      this.isPropertyAuglysingLysingExpanded = true;
    }
  }

  //#endregion

  //#region Share

  copyShareLink() {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = window.location.href;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);

    this._snackBar.open('Hlekkur afritaður', '×', {
      verticalPosition: 'top',
      duration: 3000,
      direction: 'rtl'
    });
  }

  facebookClick() {
    window.open("https://www.facebook.com/sharer.php?u=" + window.location.href, '', 'menubar=no,toolbar=no,resizable=no,scrollbars=no,height=500,width=650');
  }

  twitterClick() {
    window.open("https://twitter.com/intent/tweet?url=" + window.location.href, '', 'menubar=no,toolbar=no,resizable=no,scrollbars=no,height=500,width=650');
  }

  //#endregion

  //#region Kort

  //#region -- Globals

  public nearbyFasteignir: Array<ListPublicKaupskraThjodskraNearbyFastanumerRes> = [];
  public nearbyFasteignirMbl: Array<ListMblFasteignRes> = [];
  public nearbyFasteignirEfast: Array<ListTilSoluEignirRes> = [];
  public mapInit = false;
  public mapFetchStarted = false;

  map: any;
  layerGroup: any;
  clusterGroup: any;
  mapCenter: any;
  zoom = 15;

  mainMarker: any;
  mainCircle: any;

  // Við höldum utan um hvaða ID hafa verið BÆTT VIÐ (til að bæta ekki sama tvisvar)
  addedProperties = new Set<string>();

  // Einn popup fyrir öll merki (minna álag)
  private sharedPopup: any;

  //#endregion

  //#region -- Http fetch

  mapInitHttpFetch() {
    const intervalId = setInterval(() => {
      if (!this.mapFetchStarted) {
        if (this._fasteign) {
          this.mapHttpFetch(this._fasteign.latWgs84, this._fasteign.longWgs84);
        }
      } else {
        clearInterval(intervalId); // stoppar interval þegar mapFetchStarted = true
      }
    }, 500);
  }

  /**
   * Sækir gögn frá MBL og Kaupskrá fyrir kort.
   * Ef gögn hafa verið sótt áður þá skilar fallið strax til baka og gögn eru ekki sótt aftur.
   * Hins vegar, ef forceReload er sett sem "true" þá eru alltaf gögn sótt aftur.
   */
  mapHttpFetch(lat: number, lng: number, maxNumberOfPagesToFetch = 3) {
    try { this.ensureMainMarker(); } catch { }

    if (!this._isBrowser || !this._fasteign) return;

    // ekki sækja aftur ef þegar er til gögn eða sækjum nú þegar
    if (
      this.nearbyFasteignir.length !== 0 ||
      this.nearbyFasteignirMbl.length !== 0 ||
      (this.nearbyFasteignirEfast?.length ?? 0) !== 0 ||
      this.mapFetchStarted
    ) return;

    this.mapFetchStarted = true;

    const fetchPublicPage = (pageNumber: number, pageSize: number) =>
      this._fasteignService.publicKaupskraThjodskraAll({
        LatWgs84: lat,
        LongWgs84: lng,
        NumberOfResults: null,
        PageNumber: pageNumber,
        PageSize: pageSize
      });

    const fetchMblPage = (pageNumber: number, pageSize: number) =>
      this._fasteignService.mblFasteignNearbyLatLong({
        LatWgs84: lat,
        LongWgs84: lng,
        Fermetrar: null,
        NumberOfResults: null,
        PageNumber: pageNumber,
        PageSize: pageSize,
        IncludeLysing: false
      });

    const fetchEFasteignirPage = (pageNumber: number, pageSize: number) => {
      const Skip = (pageNumber - 1) * pageSize;
      const Take = pageSize;

      return this._fasteignService.listTilSoluFasteignir({
        Skip,
        Take,
        OrderVinsaelar: true,
        Shuffle: true,
        FasteignSkraningId: null,
        FasteignasolurId: null
      });
    };

    let totalCountEntries = 0;
    let finishCount = 0;

    const paginate = <T>(
      firstPageSize: number,
      nextPageSize: number,
      fetchPage$: (page: number, size: number) => { subscribe: Function },
      target: T[],
      render: () => void,
      maxPages = maxNumberOfPagesToFetch
    ) => {
      const run = (page: number, size: number) => {
        fetchPage$(page, size).subscribe({
          next: (items: T[]) => {
            const hasItems = !!(items && items.length);

            totalCountEntries += items.length;
            this.setPropertyLoaderCount(Math.round(totalCountEntries * 0.75));

            if (hasItems) {
              target.push(...items);
              render();

              try {
                this.setPropertyLoaderVisibility(true);
              } catch {

              }

              // ef fyrsta síðan, förum svo í "rennandi" síður nema max=1
              if (size === firstPageSize) {
                if (maxNumberOfPagesToFetch !== 1) {
                  // byrja “rennandi” fasa á síðu 1
                  run(1, nextPageSize);
                }
                return;
              }

              // “rennandi” fasi: hættum á tómu eða þegar farið yfir max
              if (size === nextPageSize && page < maxPages) {
                run(page + 1, nextPageSize);
              } else {
                this.setPropertyLoaderVisibility(false);
              }
            } else {
              finishCount++;
              if (finishCount == 3) {
                this.setPropertyLoaderVisibility(false);
              }
            }
            // ef tómt => hættum sjálfkrafa (ekkert recursive kall)
          },
          error: (err: any) => console.error('paginate error', err)
        });
      };

      // ræsum á síðu 1 með firstPageSize
      run(1, firstPageSize);
    };

    try {
      // teikna aðalmerkið
      if (!this.mapInit) {
        this.initWhenDataReady(true);
      } else {
        this.ensureMainMarker();
      }

      if (!this.nearbyFasteignirEfast) {
        this.nearbyFasteignirEfast = [];
      }

      // keyrum öll þrjú (án Promise; bara subscriptions)
      paginate(
        250,
        500,
        fetchPublicPage,
        this.nearbyFasteignir,
        () => this.renderNearbyMarkersInView()
      );

      paginate(
        250,
        500,
        fetchMblPage,
        this.nearbyFasteignirMbl,
        () => this.renderNearbyMarkersInView()
      );

      paginate(
        250,
        500,
        fetchEFasteignirPage,
        this.nearbyFasteignirEfast,
        () => this.renderNearbyMarkersInView()
      );
    } catch (err) {
      console.error('mapHttpFetch error', err);
    } finally {
    }
  }

  //#endregion

  //#region -- Main marker

  private mainColor = '#253a85';
  private retryIntervalId?: any;
  private onViewChanged?: () => void;

  private buildMainIcon() {
    return this._leafletService.L.divIcon({
      className: 'custom-circle-marker',
      html: `<div class="circle" style="width:16px;height:16px;background:${this.mainColor};border-radius:50%;border:2px solid white;"></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  }

  async initWhenDataReady(initFullscreen = false) {
    // Bíð eftir _grunneign, allt að ~15 sek (30 * 500ms)
    await this.waitFor(() => !!this._fasteign, { intervalMs: 500, maxTries: 30 });

    this.initMapAndLayers();

    if (initFullscreen) {
      this.addFullscreenControl(this.map);
      this.initPropertyLoaderControl(this.map);
      this.addFullscreenCloseControl(this.map);
      this.addRecenterControl(this.map);
      this.monitorFullScreen();
      this.watchExternalLinkClick();
    }
  }

  private waitFor(predicate: () => boolean, opts: { intervalMs: number; maxTries: number }) {
    return new Promise<void>((resolve, reject) => {
      let tries = 0;
      this.retryIntervalId = setInterval(() => {
        tries++;
        try {
          if (predicate()) {
            clearInterval(this.retryIntervalId);
            resolve();
          } else if (tries >= opts.maxTries) {
            clearInterval(this.retryIntervalId);
            reject(new Error('Timed out waiting for data'));
          }
        } catch {
          // ignora — höldum áfram að reyna
        }
      }, opts.intervalMs);
    }).finally(() => {
      if (this.retryIntervalId) clearInterval(this.retryIntervalId);
    });
  }

  private createPropertyClusterIcon(cluster: any): any {
    const childCount = cluster.getChildCount();

    // Þrjú “stærðarskref” (fínstilltu eftir smekk)
    let sizeClass = 'property-cluster-small';
    if (childCount >= 10 && childCount < 50) sizeClass = 'property-cluster-medium';
    else if (childCount >= 50) sizeClass = 'property-cluster-large';

    return this._leafletService.L.divIcon({
      html: `<div><span>${childCount}</span></div>`,
      // Mikilvægt: halda 'marker-cluster' (fyrir innbyggð behaviour), bæta okkar klösum ofan á
      className: `marker-cluster property-cluster ${sizeClass}`,
      iconSize: this._leafletService.L.point(40, 40), // fínstilla stærð ef vill
    });
  }

  private _propertyClickMarkerUnique?: any | null;

  private propertyClearClickMarkerUnique(): void {
    if (this._propertyClickMarkerUnique) {
      this.map.removeLayer(this._propertyClickMarkerUnique);
      this._propertyClickMarkerUnique = null;
    }
  }

  private propertyPlaceClickMarkerUnique(latlng: any): void {
    // alltaf hreinsa áður
    this.propertyClearClickMarkerUnique();

    this._propertyClickMarkerUnique = this._leafletService.L.marker(latlng, {
      icon: this.buildMainIcon(),
      interactive: false,            // ekki trufla smellflæði
      keyboard: false,
      bubblingMouseEvents: false,
      zIndexOffset: 10_000           // ofan á öðru
    }).addTo(this.map);
  }

  private initMapAndLayers() {
    const center = this._leafletService.L.latLng(this._fasteign.latWgs84, this._fasteign.longWgs84);
    this.mapCenter = center;

    this.map = this._leafletService.L.map('leafletMapSold', {
      center,
      zoom: this.zoom ?? 15,
      scrollWheelZoom: false,
      preferCanvas: true,
      updateWhenIdle: true,
      updateWhenZooming: false,
      attributionControl: false
    });

    // Tryggir að ef popup or opinn á korti þá sé hægt að loka honum með að smella hvar sem er á kortið, þ.e. án þess að það sé strax opnaður næsti popup strax.
    let skipNextClick = false;

    this.map.on('preclick', () => {
      const popupOpen = !!(this.sharedPopup?.isOpen && this.sharedPopup.isOpen());

      // Ef pop-up fyrir eign er opin eða cluster group er opin á korti, þá sleppa click fyrir eign á korti.
      skipNextClick = popupOpen || this.propertyClusterOpenInstant;
    });

    // Leyfir notanda að smella hvar sem er á kortið.
    this.map.on('click', async (e: any) => {
      // Alltaf fjarlægja fyrra click-merki strax
      this.propertyClearClickMarkerUnique();

      if (skipNextClick) {
        skipNextClick = false;
        return; // ekki opna nýjan popup – notandinn var að loka popupinu sem er þegar opinn.
      }
      const { lat, lng } = e.latlng;
      const geocodeRes = await this.reverseGeocode(lat, lng);
      if (!geocodeRes || !geocodeRes.street) return;

      this.propertyPlaceClickMarkerUnique({ lat, lng });

      this.triggerFullScreenOnPropertyClick(lat, lng);

      const httpReq: ExposedListPublicKaupskraThjodskraHmsSearchReq = {
        pageNumber: 1,
        pageSize: 9,
        searchText: geocodeRes.street,
        region: [+geocodeRes.postcode],
        order: "Address",
        strictSearch: true,
        addDbEntries: false
      };

      const openGrunneignPropertyPopup = (p: ExposedThjodskraLeitaFasteignRes, latlng: any) => {
        // --- Linkar á einum stað (alltaf vísa á kaupsöguna) ---
        const norm = (x: any) => x; // <- notaðu þitt eigið norm
        const kaupsagaUrl = `${this._environment.rootPublicUrl}/kaupsagan/${norm(p.FasteignNr)}`;
        const titleKaupsagaText = p.Birtingarmynd;

        // --- Info-hluti: Verðmat placeholder ---
        const detailKaupsagaHtml = `
      <div class="float-left w-100">
        <div class="verdmat-placeholder text-sm text-muted italic">Reikna verðmat…</div>
      </div>
      <span class="separator-line"></span>
    `;

        this.ensureLeafletPopupButtonStyles();

        // Mikilvægt: fastar víddir á placeholder til að koma í veg fyrir layout-hopp
        const baseGrunneignContent = `
      <div class="leaflet-popup-content min-w-[275px]">
        <div class="info-box-content relative">
          <a href="${kaupsagaUrl}" target="_blank" rel="noopener noreferrer">
            <div class="info-box-image-placeholder" style="width:360px;height:240px;"></div>
          </a>
          <h2 class="float-left w-100 !mb-1 !mt-4">${titleKaupsagaText}</h2>
          ${detailKaupsagaHtml}
          <div class="float-left w-100 mt-2 flex flex-wrap gap-2">
            <a class="orange-btn orange-btn--lg" href="${kaupsagaUrl}" target="_blank" rel="noopener noreferrer" role="button" aria-label="Skoða fasteign">
              Skoða fasteign
            </a>
          </div>
  
          <!-- Placeholder fyrir "fleiri en ein eign í húsi" skilaboð -->
          <div class="property-multiplicity-placeholder"></div>
        </div>
      </div>
    `;

        // Opna strax með grunn-HTML (engin eftirfylgni setContent í async)
        this.sharedPopup
          .setLatLng(latlng)
          .setContent(baseGrunneignContent)
          .openOn(this.map);

        // --- Popup guard: aðeins uppfæra ef þetta popup er enn opið
        const popupToken = `${Date.now()}-${Math.random()}`;
        const setPopupToken = () => {
          const el = this.sharedPopup.getElement();
          if (el) el.setAttribute('data-popup-token', popupToken);
        };
        const isCurrentPopup = () =>
          this.sharedPopup.getElement()?.getAttribute('data-popup-token') === popupToken;

        setPopupToken();

        // --- DOM-only helper: engin strengja-fallback, engin setContent í async
        const replaceDom = (selector: string, replacementHtml: string) => {
          const el = this.sharedPopup.getElement();
          if (!el) {
            requestAnimationFrame(() => replaceDom(selector, replacementHtml));
            return;
          }
          const node = el.querySelector(selector);
          if (!node) return;
          const n = node as HTMLElement;
          if (n.outerHTML === replacementHtml) return; // idempotent
          n.outerHTML = replacementHtml;
        };

        // --- Idempotent innsetningar (einu sinni)
        const insertCoverOnce = (url: string) => {
          if (!isCurrentPopup()) return;
          const el = this.sharedPopup.getElement();
          if (!el) return;

          // Ef mynd er þegar komin, ekki gera neitt
          if (el.querySelector('.info-box-image')) return;

          // Preload áður en skipt er inn
          const img = new Image();
          (img as any).decoding = 'async';
          img.loading = 'eager';
          img.width = 360;
          img.height = 240;
          img.alt = 'Ljósmynd';
          img.className = 'info-box-image';
          img.onload = () => {
            if (!isCurrentPopup()) return;
            replaceDom(
              '.info-box-image-placeholder',
              `<img src="${url}" alt="Ljósmynd" class="info-box-image" width="360" height="240" loading="eager" />`
            );
            setPopupToken(); // ef Leaflet “refreshar” content
          };
          img.onerror = () => { /* halda placeholder */ };
          img.src = url;
        };

        const renderVerdmatOnce = (innerHtml: string) => {
          if (!isCurrentPopup()) return;
          const el = this.sharedPopup.getElement();
          if (!el) return;
          if (el.querySelector('.verdmat-result')) return; // þegar sett
          const node = el.querySelector('.verdmat-placeholder');
          if (!node) return;
          (node as HTMLElement).outerHTML = `<div class="verdmat-result float-left w-100">${innerHtml}</div>`;
        };

        // --- NÝTT: Multiplicity helpers (einkvæð nöfn með "property") ---
        const buildPropertyHouseAllUrlGrunneignUnique = (heimilisfang: string, postnumer: string) => {
          const heimilisfangEnc = encodeURIComponent((heimilisfang ?? '').toString().trim());
          const postnumerEnc = encodeURIComponent((postnumer ?? '').toString().trim());
          // Sama mynstrið og í hinu: vísa á eignalista með query params fyrir address & postal
          return `${this._environment.rootPublicUrl}/kaupsagan/eignalisti?address=${heimilisfangEnc}&postal=${postnumerEnc}`;
        };

        const renderPropertyMultiplicityOnceGrunneignUnique = (innerHtml: string) => {
          if (!isCurrentPopup()) return;
          const el = this.sharedPopup.getElement();
          if (!el) return;
          const node = el.querySelector('.property-multiplicity-placeholder');
          if (!node) return;
          (node as HTMLElement).outerHTML = innerHtml; // idempotent að því leyti að við skrifum einu sinni á placeholder
        };

        const startPropertyMultiplicityCheckGrunneignUnique = () => {
          // Reynum að finna sanngjarnt heimilisfang + póstnúmer
          const heimilisfang = (p as any).Heimilisfang ?? titleKaupsagaText ?? '';
          const postnumer = (p as any).PostNr ?? '';
          const fasteignNr = p.FasteignNr;

          // Ef við höfum ekki gögn, fjarlægjum einfaldlega placeholder
          if (!heimilisfang || !postnumer) {
            renderPropertyMultiplicityOnceGrunneignUnique('');
            return;
          }

          // Kalla á sömu þjónustu og í fyrri kóða (Observable<boolean>)
          this.checkMoreThanOneProperty(heimilisfang.toString().trim(), postnumer.toString().trim()).subscribe({
            next: (hasMultiple: boolean) => {
              if (!isCurrentPopup()) return;

              if (hasMultiple) {
                const allUrl = buildPropertyHouseAllUrlGrunneignUnique(heimilisfang, postnumer);
                const multiplicityHtml = `
                  <span class="separator-line mt-3 mb-3 block"></span>
                  <p class="mt-2 float-left w-100 text-[15px] text-gray-800 !mb-0">
                    <a
                      href="${allUrl}"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="underline text-gray-800 hover:text-gray-900 italic"
                      aria-label="Skoða allar eignir í húsi"
                    >
                      Skoða allar eignir í húsi
                    </a>
                  </p>
                `;

                renderPropertyMultiplicityOnceGrunneignUnique(multiplicityHtml);
              } else {
                // Fjarlægja placeholder ef ekkert að sýna
                renderPropertyMultiplicityOnceGrunneignUnique('');
              }
            },
            error: () => {
              // Þögn á villu: fjarlægja placeholder
              renderPropertyMultiplicityOnceGrunneignUnique('');
            }
          });
        };

        // --- Verðmat (async, Lat/Long) — DOM-only uppfærsla
        const verdmatErrorHtml = `<p class="text-sm text-muted mt-1 mb-1 italic">Ekki tókst að reikna verðmat</p>`;

        const onVerdmatKaupsagaError = () => {
          if (!isCurrentPopup()) return;
          renderVerdmatOnce(verdmatErrorHtml);
        };

        const onVerdmatKaupsagaBackupDone = (verdmat: any) => {
          if (!isCurrentPopup()) return;
          try {
            renderVerdmatOnce(`
          <p class="!mt-1 !mb-1">
            Fermetraverð í hverfi: ${this.formatVerd((verdmat.estimatedValueSqm * 1000).toString())} kr./m²
          </p>
        `);
          } catch {
            onVerdmatKaupsagaError();
          }
        };

        this._fasteignService
          .estimatePropertyValueLatLong({ LatWgs84: p.Lat, LongWgs84: p.Long })
          .subscribe({ next: onVerdmatKaupsagaBackupDone, error: onVerdmatKaupsagaError });

        // --- Forsíðumynd (async) — preload + DOM-only skipti, föst vídd
        this._fasteignService.kaupskraSaekjaProfilMynd({
          Fastnum: norm(p.FasteignNr),
          Lat: p.Lat,
          Lng: p.Long,
          Postnumer: p.PostNr
        }).subscribe((forsiduMyndUrl: string) => {
          if (!isCurrentPopup() || !forsiduMyndUrl) return;
          insertCoverOnce(forsiduMyndUrl);
        });

        // --- NÝTT: ræsa leit á "fleiri en ein eign í húsi" (async, DOM-only) ---
        startPropertyMultiplicityCheckGrunneignUnique();
      };

      this._fasteignService.listPublicKaupskraThjodskraHmsSearch(httpReq).subscribe(properties => {
        try {
          const p = properties[0];

          // Sýna HTML popup þegar hann er valinn handvirkt á korti
          openGrunneignPropertyPopup(p, { lat, lng });
        } catch (e) {
          console.log(e);
        }
      })
    });

    this._leafletService.L.control.attribution({ position: 'bottomleft' }).addTo(this.map);

    this._leafletService.L
      .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; Kortavinnsla <a target="_blank" href="https://www.e-fasteignir.is">e-fasteignir</a>',
      })
      .addTo(this.map);

    // this._leafletService.L er ekki alltaf tilbúið, reynum 10 sinnum á sek fresti.
    if (!this.clusterGroup) {
      let attemptsClusterGroupInit = 0;
      const interval = setInterval(() => {
        attemptsClusterGroupInit++;

        if (!this.clusterGroup && this.map && this._leafletService?.L) {
          this.clusterGroup = this._leafletService.L.markerClusterGroup({
            chunkedLoading: true,
            chunkInterval: 40,
            chunkDelay: 16,
            removeOutsideVisibleBounds: true,
            spiderfyOnEveryZoom: false,
            zoomToBoundsOnClick: true,
            animateAddingMarkers: false,
            maxClusterRadius: (zoom: number) => {
              const { lat } = this.map.getCenter();
              const pxFor10m = this.metersToPixels(10, lat, zoom);
              const pxMin = this.minPixelRadiusForZoom(zoom);
              return Math.max(pxFor10m, pxMin);
            },
            spiderfyOnMaxZoom: true,
            iconCreateFunction: this.createPropertyClusterIcon.bind(this),
          });

          // ▶ Fylgjumst með hvenær cluster er "opinn"
          this.clusterGroup.on('spiderfied', () => {
            this.propertyClusterSetOpenWithDelay(true);
          });

          this.clusterGroup.on('unspiderfied', () => {
            this.propertyClusterSetOpenWithDelay(false);
          });

          // Ef þú vilt líka “örugga” stöðuna eftir zoom-end (t.d. ef plugin breytir einhverju þar)
          this.map.on('zoomend', () => {
            // hér gætirðu, ef þörf er á, endurmetið stöðuna og kallað aftur:
            // this.propertyClusterSetOpenWithDelay( ...núverandi staða... );
            // en í flestum tilfellum duga spiderfied/unspiderfied events.
          });

          this.map.addLayer(this.clusterGroup);
          clearInterval(interval);
        }

        if (attemptsClusterGroupInit >= 10) {
          clearInterval(interval);
        }
      }, 1000);
    }

    // EINN popup fyrir öll merki
    this.sharedPopup = this._leafletService.L.popup({
      offset: this._leafletService.L.point(0, -30)
    });

    // Búum til/uppfærum aðalmerki + hring og miðjum kortið
    this.ensureMainMarker({
      draggable: true,
      followMode: 'drag',
      animate: true,
      circleRadius: 250,
      recenterZoom: this.zoom ?? 15,
    });

    // Fyrsta hleðsla á nálægum merkjum
    this.renderNearbyMarkersInView();

    // Debounced uppfærsla
    const debounced = this.debounce(() => this.renderNearbyMarkersInView(), 120);
    this.onViewChanged = () => debounced();
    this.map.on('moveend zoomend', this.onViewChanged);

    this.mapInit = true;
  }

  private debounce<T extends (...args: any[]) => void>(fn: T, wait: number) {
    let t: any;
    return (...args: Parameters<T>) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  /** Býr til eða uppfærir aðalmerki + hring og sér um drag-fylgni og miðjun. */
  private ensureMainMarker(options?: {
    draggable?: boolean;
    followMode?: 'drag' | 'dragend';
    animate?: boolean;
    circleRadius?: number;
    recenterZoom?: number;
  }, centerMap = true) {
    const {
      draggable = true,
      followMode = 'drag',
      animate = true,
      circleRadius = 250,
      recenterZoom = 15,
    } = options ?? {};

    const center = this._leafletService.L.latLng(this._fasteign.latWgs84, this._fasteign.longWgs84);
    this.mapCenter = center;

    // Merki
    if (!this.mainMarker) {
      this.mainMarker = this._leafletService.L.marker(center, {
        icon: this.buildMainIcon(),
        draggable,
      }).addTo(this.map);
    } else {
      this.mainMarker.setLatLng(center);
      if (draggable) this.mainMarker.dragging?.enable();
      else this.mainMarker.dragging?.disable();
    }

    // Hringur
    if (!this.mainCircle) {
      this.mainCircle = this._leafletService.L.circle(center, {
        radius: circleRadius,
        color: this.mainColor,
        fillColor: this.mainColor,
        fillOpacity: 0.2,
      }).addTo(this.map);
    } else {
      this.mainCircle.setLatLng(center);
      this.mainCircle.setRadius(circleRadius);
    }

    // Miðja kort
    if (centerMap) {
      this.centerMapOn(center, { animate: true, zoom: recenterZoom });
    } else {
      // Tryggir að ef kort var ekki miðjað að smá hreyfing eigi sér stað.
      // Þetta þarf þannig að nóður uppfærist strax eftir að filter hafi verið gert í framhenda.
      // Þegar kortið er miðjað þá á sér stað hreyfing og þess vegna þarf þetta aðeins ef kortið er ekki miðjað.
      requestAnimationFrame(() => {
        const c = this.map.getCenter();
        const z = this.map.getZoom();
        this.map.setView(c, z, { animate: false });
      });
    }

    // Event-handlerar (fjarlægjum fyrst)
    this.mainMarker.off('drag');
    this.mainMarker.off('dragend');

    if (followMode === 'drag') {
      this.mainMarker.on('drag', (e: any) => {
        const ll = e.target.getLatLng();
        this.mainCircle?.setLatLng(ll);
        this.centerMapOn(ll, { animate: false });
      });
    }

    this.mainMarker.on('dragend', (e: any) => {
      const ll = e.target.getLatLng();
      this.mainCircle?.setLatLng(ll);
      this.centerMapOn(ll, { animate });
      // TODO: vista stöðu ef þarf
      // this.selectedMapFasteign.latWgs84 = ll.lat;
      // this.selectedMapFasteign.longWgs84 = ll.lng;
    });
  }

  /** Hjálparfall til að miðja kortið á tiltekinn punkt. */
  private centerMapOn(latlng: any, opts?: { animate?: boolean; zoom?: number }) {
    const animate = opts?.animate ?? true;
    const zoom = opts?.zoom ?? this.map?.getZoom?.() ?? this.zoom ?? 15;
    this.map.setView(latlng, zoom, { animate });
  }

  // Lágmarks-pixla-radius sem tryggir grúbbun þegar zoomað er út
  minPixelRadiusForZoom(zoom: number): number {
    if (zoom >= 17) return 0;
    if (zoom >= 15) return 50;
    if (zoom >= 14) return 60;
    if (zoom >= 12) return 80;
    if (zoom >= 10) return 90;
    return 90;
  }

  /** Kalla við destroy/teardown til að losa handlera og tíma. */
  teardownMap() {
    if (this.retryIntervalId) clearInterval(this.retryIntervalId);
    if (this.map && this.onViewChanged) {
      this.map.off('moveend zoomend', this.onViewChanged);
    }
  }

  //#endregion

  //#region -- General markers

  private fastanumToMarker = new Map<string, { layer: any; source: 'efast' | 'mbl' | 'kaup' | 'grunn'; lat: number; lng: number }>();
  kaupsamningar = true;
  desiredFastnums: any;

  renderNearbyMarkersInView(): void {

    if (!this.map || !this.clusterGroup) return;

    //#region Globals and helpers

    const verdmatInfoUrl = `${this._environment.rootPublicUrl}/verdmat-utskyring`;

    const pad = 0.20;
    const bounds = this.map.getBounds().pad(pad);
    const norm = (v: any) => (v == null ? '' : v.toString().trim());
    const inView = (lat?: number, lng?: number) => {
      if (lat == null || lng == null) return false;
      return bounds.contains(this._leafletService.L.latLng(lat, lng));
    };
    const samePos = (aLat: number, aLng: number, bLat: number, bLng: number) => {
      // smá þolmörk til að losna við floating-diff (≈ ~1 cm)
      return Math.abs(aLat - bLat) < 1e-7 && Math.abs(aLng - bLng) < 1e-7;
    };

    // Icons
    const forSaleMblIcon = this._leafletService.L.icon({
      iconUrl: this._environment.rootPublicUrl + '/assets/images/others/markers/new/for-sale.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });

    const forSaleEfasteignirIcon = this._leafletService.L.icon({
      iconUrl: this._environment.rootPublicUrl + '/assets/images/others/markers/new/for-sale-e.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });

    // --- Index eftir fastanúmeri, filterað í view ---
    // -- Athugaðu, ef sama fasteign (með sama fastanúmeri) kemur fyrir oftar en einu sinni t.d. hjá MBL, þá er bara ein nóða sett inn fyrir tiltekið fastanúmer.
    // -- Það er af því við erum að vinna með Set í typescript.
    const efastByFn = new Map<string, ListTilSoluEignirRes>();
    for (const p of this.nearbyFasteignirEfast ?? []) {
      const fn = norm(p.fasteignNr);
      if (!fn) continue;
      if (!inView(p.laT_WGS84, p.lonG_WGS84)) continue;
      efastByFn.set(fn, p);
    }

    const mblByFn = new Map<string, ListMblFasteignRes>();
    for (const p of this.nearbyFasteignirMbl ?? []) {
      const fn = norm(p.fastanumerShort);
      if (!fn) continue;
      if (!inView(p.latitude, p.longitude)) continue;
      mblByFn.set(fn, p);
    }

    const kaupByFn = new Map<string, ListPublicKaupskraThjodskraNearbyFastanumerRes>();
    for (const p of this.nearbyFasteignir ?? []) {
      const fn = norm(p.fastnum);
      if (!fn) continue;
      if (!inView(p.latWgs84, p.longWgs84)) continue;
      kaupByFn.set(fn, p);
    }

    const grunnEignFn = new Map<string, ReadPublicKaupskraThjodskraRes>();
    grunnEignFn.set(norm(this._fasteign.fastnum), this._fasteign);

    if (this._fasteign?.einflm) {
      // efastByFn -> flatarmal
      for (const [fn, p] of Array.from(efastByFn.entries())) {
        const fm = p.flatarmal;
        if (fm < this._fasteign?.einflm - 25) { efastByFn.delete(fn); continue; }
        if (fm > this._fasteign?.einflm + 25) { efastByFn.delete(fn); continue; }
      }

      // mblByFn -> fermetrar
      for (const [fn, p] of Array.from(mblByFn.entries())) {
        const fm = p.fermetrar;
        if (fm < this._fasteign?.einflm - 25) { mblByFn.delete(fn); continue; }
        if (fm > this._fasteign?.einflm + 25) { mblByFn.delete(fn); continue; }
      }

      // kaupByFn -> einflm
      for (const [fn, p] of Array.from(kaupByFn.entries())) {
        const fm = p.einflm;
        if (fm < this._fasteign?.einflm - 25) { kaupByFn.delete(fn); continue; }
        if (fm > this._fasteign?.einflm + 25) { kaupByFn.delete(fn); continue; }
      }
    }

    //#endregion

    //#region e-fasteignir eign

    // Býr til pop-up fyrir fasteign skráða til sölu hjá e-fasteignum
    const makeEfastMarker = (p: ListTilSoluEignirRes) => {
      const marker = this._leafletService.L.marker([p.laT_WGS84, p.lonG_WGS84], {
        icon: forSaleEfasteignirIcon
      });

      marker.on('click', () => {
        this.triggerFullScreenOnPropertyClick(p.laT_WGS84, p.lonG_WGS84);

        // --- Helper: skipta út í renderuðu DOM; strengja-fallback ef DOM ekki komið ---
        const replaceInPopupBySelector = (selector: string, replacementHtml: string, fallbackRegex?: RegExp) => {
          const el = this.sharedPopup.getElement();
          if (el) {
            const node = el.querySelector(selector);
            if (node) {
              (node as HTMLElement).outerHTML = replacementHtml;
              return true;
            }
          }
          const current = this.sharedPopup.getContent() as any;
          if (typeof current === 'string' && fallbackRegex) {
            const updated = current.replace(fallbackRegex, replacementHtml);
            if (updated !== current) {
              this.sharedPopup.setContent(updated);
              return true;
            }
          }
          return false;
        };

        const replaceFasteignamat = (html: string) =>
          replaceInPopupBySelector(
            '.fasteignamat-placeholder',
            html,
            // leyfa auka class/attributa og whitespace
            /<[^>]*class="[^"]*fasteignamat-placeholder[^"]*"[^>]*>[\s\S]*?<\/[^>]+>/
          );

        const replaceVerdmat = (html: string) =>
          replaceInPopupBySelector(
            '.verdmat-placeholder',
            html,
            /<div class="verdmat-placeholder[^>]*>\s*<\/div>/
          );

        // --- Einkvæmir helperar fyrir multiplicity (Efast) ---
        const buildHouseAllPropertiesUrlPropertyUniqueEfast = (heimilisfang: string, postnumer?: string | number | null): string => {
          const q = encodeURIComponent((heimilisfang ?? '').trim());
          const pn = encodeURIComponent((postnumer ?? '').toString().trim());
          return `${this._environment.rootPublicUrl}/kaupsagan/eignalisti?address=${q}&postal=${pn}`;
        };

        const buildKaupsagaMultiplicityUrlPropertyUniqueEfast = (
          fasteignNr?: string | number | null,
          heimilisfang?: string,
          postnumer?: string | number | null
        ): string => {
          const q = encodeURIComponent((heimilisfang ?? '').trim());
          const pn = encodeURIComponent((postnumer ?? '').toString().trim());
          return `${this._environment.rootPublicUrl}/kaupsagan/eignalisti?address=${q}&postal=${pn}`;
        };

        // --- Finna tengda kaupsögu / MBL nálægt ---
        const kaup = this.nearbyFasteignir?.find(x => norm(x.fastnum) === norm(p.fasteignNr)) ?? null;
        const mbl = this.nearbyFasteignirMbl?.find(x => norm(x.fastanumerShort) === norm(p.fasteignNr)) ?? null;

        const kaupverdStr =
          (kaup && kaup.kaupverd != null && kaup.kaupverd !== 0)
            ? this.formatToMillionKr((kaup.kaupverd * 1000) as number)
            : '—';

        const thingStr =
          (kaup && kaup.thinglystDags)
            ? this.formatDate(new Date(kaup.thinglystDags))
            : '—';

        // Placeholder fyrir Fasteignamat ríkisins (verður skipt út)
        const fasteignamatPlaceholder = `
        <p class="!mt-1 !mb-1 fasteignamat-placeholder">
          Fasteignamat ríkisins: Sæki fasteignamat…
        </p>
      `;

        const kaupHtml = `
        <div class="float-left w-100">
          ${kaup ? `<p class="!mt-1 !mb-1">Kaupverð (${thingStr}): ${kaupverdStr}</p>` : ``}
          ${fasteignamatPlaceholder}
          <div class="verdmat-placeholder text-sm text-muted italic">Reikna verðmat…</div>
        </div>
        <span class="separator-line"></span>
      `;

        // Linkar
        const kaupsagaUrl = `${this._environment.rootPublicUrl}/kaupsagan/${norm(p.fasteignNr)}`;
        const solusidaUrl = `${this._environment.rootPublicUrl}/til-solu/${norm(p.fasteignSkraningId)}`;

        // Titill: "Heimilisfang, 123 m²" ef stærð er til
        const titleText = `${p.fasteign ?? 'Óþekkt heimilisfang'}${p?.flatarmal ? ', ' + p.flatarmal.toString() + ' m²' : ''}`;

        // Opið hús (undir Auglýstu verði) ef til
        const opidHusHtml = p.opidHus
          ? `<p class="!mt-1 !mb-1">Opið hús: ${this.formatDate(new Date(p.opidHus as any))}</p>`
          : '';

        this.ensureLeafletPopupButtonStyles();

        // --- Multiplicity placeholder (bætt við) ---
        const multiplicityPlaceholder = `
          <div class="property-multiplicity-placeholder">
          </div>
        `;

        const baseContent = `
    <div class="leaflet-popup-content min-w-[275px]">
      <div class="info-box-content relative">
        <a href="${kaupsagaUrl}" target="_blank" rel="noopener noreferrer">
          <img
            src="${this._environment.fileServiceUrl}FasteignirMyndir/${p.forsidumyndAzureName}"
            alt="Ljósmynd"
            class="info-box-image"
          />
        </a>
  
        <h2 class="float-left w-100 !mb-1 !mt-2">${titleText}</h2>
  
        <p class="!mt-1 !mb-1">
          Auglýst verð (${this.formatDate(new Date(p.efasteignirTimeStamp as any))}):
          ${p.asettVerd != null ? this.formatToMillionKr(p.asettVerd as number) : 'Tilboð'}
        </p>
  
        ${opidHusHtml}
        <span class="separator-line"></span>
        ${kaupHtml}
  
        <div class="float-left w-100 mt-2 flex flex-wrap items-end gap-2 justify-between">
          <!-- Vinstri hluti: takkarnir -->
          <div class="flex flex-wrap gap-2">
            <a
              class="orange-btn orange-btn--lg"
              href="${solusidaUrl}"
              target="_blank"
              rel="noopener noreferrer"
              role="button"
              aria-label="Skoða"
            >
              Sölusíða
            </a>
            <a
              class="blue-btn blue-btn--lg"
              href="${kaupsagaUrl}"
              target="_blank"
              rel="noopener noreferrer"
              role="button"
              aria-label="Sölusíða"
            >
              Kaupsagan
            </a>
          </div>
  
          <!-- Hægri hluti: táknin, birtast aðeins ef kaup eða mbl eru true -->
          <div class="flex items-end gap-2 ml-auto">
            ${mbl
            ? `
                <a
                  href="https://mbl.is/fasteignir/fasteign/${mbl.mblPk}"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Opna MBL"
                >
                  <img
                    src="/assets/images/icons/new/mbl.png"
                    alt="MBL"
                    class="h-6 w-auto self-end"
                  />
                </a>
              `
            : ''
          }
            ${kaup
            ? `
                <a
                  href="${kaupsagaUrl}"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Skoða kaupsögu"
                >
                  <img
                    src="/assets/images/icons/new/logo-sm.svg"
                    alt="Kaupsaga"
                    class="h-6 w-auto self-end"
                  />
                </a>
              `
            : ''
          }
          </div>
        </div>
  
        ${multiplicityPlaceholder}
      </div>
    </div>
  `;

        this.sharedPopup.setLatLng(marker.getLatLng()).setContent(baseContent).openOn(this.map);

        // --- Popup guard: merkjum opinn popup með token svo async-svör fari á réttan ---
        const popupToken = `${Date.now()}-${Math.random()}`;
        const setPopupToken = () => {
          const el = this.sharedPopup.getElement();
          if (el) el.setAttribute('data-popup-token', popupToken);
        };
        const isCurrentPopup = () =>
          this.sharedPopup.getElement()?.getAttribute('data-popup-token') === popupToken;

        setPopupToken();

        // --- 1) Fasteignamat ríkisins: sækjum og uppfærum DOM (með guard) ---
        const onFasteignamatDone = (res: any) => {
          if (!isCurrentPopup()) return;
          const mat = kaup?.fasteignamatGildandi ? kaup?.fasteignamatGildandi * 1000 : res?.fasteignamat;
          const html = (mat != null && isFinite(mat) && mat > 0)
            ? `<p class="!mt-1 !mb-1">Fasteignamat í dag: ${this.formatToMillionKr(mat as number)}</p>`
            : `<p class="!mt-1 !mb-1">Fasteignamat í dag: —</p>`;
          replaceFasteignamat(html);
        };

        const onFasteignamatError = () => {
          if (!isCurrentPopup()) return;
          replaceFasteignamat(`<p class="!mt-1 !mb-1">Fasteignamat í dag: Ekki tókst að sækja</p>`);
        };

        this._fasteignService
          .fasteignYfirlit(p.fasteignSkraningId, '')
          .subscribe({ next: onFasteignamatDone, error: onFasteignamatError });

        // --- 2) Verðmat (heild): estimatedValueSqm * flatarmál * 1000 ---
        const hasEinflm =
          typeof p.flatarmal === 'number' && Number.isFinite(p.flatarmal) && p.flatarmal > 0;
        const fastnum = p.fasteignNr?.toString();

        const verdmatErrorHtml = `<p class="text-sm text-muted italic">Ekki tókst að reikna verðmat</p>`;

        const onVerdmatError = () => {
          if (!isCurrentPopup()) return;
          replaceVerdmat(verdmatErrorHtml);
        };

        const onVerdmatDone = (verdmat: any) => {
          if (!isCurrentPopup()) return;
          try {
            const hasSqm = verdmat?.estimatedValueSqm != null && isFinite(verdmat.estimatedValueSqm);
            const styrkleiki = verdmat?.estimateStrength == 1 ? 'sterkt' : 'miðlungs';

            const html = hasSqm && hasEinflm
              ? `
                <div class="float-left w-100">
                  <p class="!mt-1 !mb-1">
                    Verðmat í dag (${styrkleiki}): ${this.formatToMillionKr(verdmat.estimatedValueSqm * p.flatarmal * 1000)}
                    <a href="${verdmatInfoUrl}" target="_blank" class="c-link">
                      <i class="fas fa-info-circle text-[12px] hover:cursor-pointer relative -top-px primary-color"
                         title="Upplýsingar um útreikning verðmats"></i>
                    </a>
                  </p>
                </div>
              `
              : verdmatErrorHtml;

            replaceVerdmat(html);
          } catch {
            onVerdmatError();
          }
        };

        if (hasEinflm && fastnum) {
          this._fasteignService
            .estimatePropertyValue(fastnum)
            .subscribe({ next: onVerdmatDone, error: onVerdmatError });
        } else {
          this._fasteignService
            .estimatePropertyValueLatLong({ LatWgs84: p.laT_WGS84, LongWgs84: p.lonG_WGS84 })
            .subscribe({ next: onVerdmatDone, error: onVerdmatError });
        }

        // --- 3) Fleiri en ein fasteign í húsi? (sams konar lógík og í Kaupsögu/MBL) ---
        const heimilisfangForMultiplicity = (p.fasteign ?? titleText ?? '').toString().trim();
        // p hefur oft ekki postnumer; reynum að ná því úr p eða nálægum heimildum
        const postnumerForMultiplicity =
          (p as any)?.postNumer ?? (p as any)?.postnumer ?? (kaup as any)?.postNumer ?? (mbl as any)?.postNumer ?? '';

        this.checkMoreThanOneProperty(heimilisfangForMultiplicity, postnumerForMultiplicity).subscribe({
          next: (hasMultiple: boolean) => {
            if (!isCurrentPopup()) return;
            if (hasMultiple) {
              const url = buildKaupsagaMultiplicityUrlPropertyUniqueEfast(
                (kaup as any)?.FasteignNr ?? null,
                heimilisfangForMultiplicity,
                postnumerForMultiplicity
              );

              const multiplicityHtml = `
              <span class="separator-line mt-3 mb-3 block"></span>
              <p class="mt-2 float-left w-100 text-[15px] text-gray-800 !mb-0">
                <a
                  href="${url}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-gray-800 hover:text-gray-900 italic"
                  aria-label="Skoða allar eignir í húsi"
                >
                  Skoða allar eignir í húsi
                </a>
              </p>
            `;

              replaceInPopupBySelector(
                '.property-multiplicity-placeholder',
                multiplicityHtml,
                /<div class="property-multiplicity-placeholder[^>]*><\/div>/
              );
            } else {
              // Fjarlægjum placeholder ef ekkert að sýna
              replaceInPopupBySelector(
                '.property-multiplicity-placeholder',
                '',
                /<div class="property-multiplicity-placeholder[^>]*><\/div>/
              );
            }
          },
          error: () => {
            if (!isCurrentPopup()) return;
            // Þögn á villu: fjarlægja placeholder
            replaceInPopupBySelector(
              '.property-multiplicity-placeholder',
              '',
              /<div class="property-multiplicity-placeholder[^>]*><\/div>/
            );
          }
        });
      });

      return marker;
    };

    //#endregion

    //#region Mbl eign

    // Býr til pop-up fyrir fasteign skráða til sölu hjá MBL
    const makeMblMarker = (p: ListMblFasteignRes) => {
      const marker = this._leafletService.L.marker([p.latitude!, p.longitude!], {
        icon: forSaleMblIcon
      });

      marker.on('click', () => {
        this.triggerFullScreenOnPropertyClick(p.latitude!, p.longitude!);

        // --- Helpers: skipta út í DOM eða streng (fallback) ---
        const replaceInPopupBySelector = (selector: string, replacementHtml: string, fallbackRegex?: RegExp) => {
          const el = this.sharedPopup.getElement();
          if (el) {
            const node = el.querySelector(selector);
            if (node) {
              (node as HTMLElement).outerHTML = replacementHtml;
              return true;
            }
          }
          const current = this.sharedPopup.getContent() as any;
          if (typeof current === 'string' && fallbackRegex) {
            const updated = current.replace(fallbackRegex, replacementHtml);
            if (updated !== current) {
              this.sharedPopup.setContent(updated);
              return true;
            }
          }
          return false;
        };

        const replaceVerdmat = (html: string) =>
          replaceInPopupBySelector(
            '.verdmat-placeholder',
            html,
            /<div class="verdmat-placeholder[^>]*>\s*<\/div>/
          );

        const buildKaupsagaMultiplicityUrlPropertyUnique = (
          fasteignNr?: string | number | null,
          heimilisfang?: string,
          postnumer?: string | number | null
        ): string => {
          const q = encodeURIComponent((heimilisfang ?? '').trim());
          const pn = encodeURIComponent((postnumer ?? '').toString().trim());

          return `${this._environment.rootPublicUrl}/kaupsagan/eignalisti?address=${q}&postal=${pn}`;
        };

        // --- Finna tengda kaupsögu (nota fastanumerShort) ---
        const kaup = this.nearbyFasteignir?.find(x => norm(x.fastnum) === norm(p.fastanumerShort)) ?? null;

        // --- Velja fasteignamat (forgangur: kaupsaga.fasteignamat -> p.fasteignamat) ---
        const matFromKaupsaga = (kaup as any)?.fasteignamat;
        let chosenMat: number | null =
          (matFromKaupsaga != null && isFinite(matFromKaupsaga)) ? Number(matFromKaupsaga)
            : (p.fasteignamat != null && isFinite(p.fasteignamat)) ? Number(p.fasteignamat)
              : null;

        // Ef talið er í þúsundum, normalíserum í krónur
        if (chosenMat != null && chosenMat > 0 && chosenMat < 1_000_000) {
          chosenMat = chosenMat * 1000;
        }

        const fasteignamatHtml = `
        <p class="!mt-1 !mb-1">
          Fasteignamat í dag: ${chosenMat != null && chosenMat > 0 ? this.formatToMillionKr(chosenMat) : '—'}
        </p>
      `;

        const kaupverdStr = (kaup && kaup.kaupverd != null && kaup.kaupverd !== 0)
          ? this.formatToMillionKr((kaup.kaupverd * 1000) as number)
          : '—';

        const thingStr = (kaup && kaup.thinglystDags)
          ? this.formatDate(new Date(kaup.thinglystDags))
          : '—';

        // --- Linkar á einum stað (alltaf vísa á kaupsöguna) ---
        const kaupsagaUrl = `${this._environment.rootPublicUrl}/kaupsagan/${norm(p.fastanumerShort)}`;
        const mblUrl = `https://mbl.is/fasteignir/fasteign/${p.mblPk}`;

        // --- Titill "Heimilisfang, 123 m²" ef stærð er til (alltaf nota MBL fermetra) ---
        const titleText = `${p.heimilisfang}${p?.fermetrar ? ', ' + p.fermetrar.toString() + ' m²' : ''}`;

        // --- KaupHtml (engin placeholder fyrir fasteignamat – setjum strax inn) ---
        const kaupHtml = `
        <div class="float-left w-100">
          ${kaup ? `<p class="!mt-1 !mb-1">Kaupverð (${thingStr}): ${kaupverdStr}</p>` : ``}
          ${fasteignamatHtml}
          <div class="verdmat-placeholder text-sm text-muted italic">Reikna verðmat…</div>
        </div>
        <span class="separator-line"></span>
      `;

        this.ensureLeafletPopupButtonStyles();

        // --- Multiplicity placeholder (bætt við) ---
        const multiplicityPlaceholder = `
          <div class="property-multiplicity-placeholder">
          </div>
        `;

        const baseContent = `
            <div class="leaflet-popup-content min-w-[275px]">
              <div class="info-box-content relative">
                <a href="${kaupsagaUrl}" target="_blank" rel="noopener noreferrer">
                  <div class="info-box-image-placeholder"></div>
                </a>
  
                <h2 class="float-left w-100 !mb-1 !mt-2">${titleText}</h2>
  
                <p class="!mt-1 !mb-1">
                  Auglýst verð${p.skrad ? ` (${this.formatDate(new Date(p.skrad))})` : ''}:
                  ${p.verd != null ? this.formatToMillionKr(p.verd as number) : 'Tilboð'}
                </p>
  
                <span class="separator-line"></span>
                ${kaupHtml}
  
                <div class="float-left w-100 mt-2 flex flex-wrap items-end justify-between gap-2">
                  <a
                    class="orange-btn orange-btn--lg"
                    href="${kaupsagaUrl}"
                    target="_blank"
                    rel="noopener noreferrer"
                    role="button"
                    aria-label="Skoða fasteign"
                  >
                    Skoða fasteign
                  </a>
  
                  <div class="flex items-end gap-2 ml-auto">
                    <a
                      href="${mblUrl}"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Opna MBL"
                    >
                      <img
                        src="/assets/images/icons/new/mbl.png"
                        alt="mbl"
                        class="h-6 w-auto self-end"
                      />
                    </a>
  
                    ${kaup
            ? `
                          <a
                            href="${kaupsagaUrl}"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Skoða kaupsögu"
                          >
                            <img
                              src="/assets/images/icons/new/logo-sm.svg"
                              alt="kaupsaga"
                              class="h-6 w-auto self-end"
                            />
                          </a>
                        `
            : ''
          }
                  </div>
                </div>
  
                ${multiplicityPlaceholder}
              </div>
            </div>
      `;

        this.sharedPopup
          .setLatLng(marker.getLatLng())
          .setContent(baseContent)
          .openOn(this.map);

        // --- Popup guard: merkjum núverandi popup með token ---
        const popupToken = `${Date.now()}-${Math.random()}`;
        const setPopupToken = () => {
          const el = this.sharedPopup.getElement();
          if (el) el.setAttribute('data-popup-token', popupToken);
        };
        const isCurrentPopup = () =>
          this.sharedPopup.getElement()?.getAttribute('data-popup-token') === popupToken;

        // Merkja eftir að popup er renderað
        setPopupToken();

        // --- Verðmat (kr. heild): estimatePropertyValue(fastanumerShort) * fermetrar ---
        const hasSqmInput = p?.fermetrar != null && isFinite(p.fermetrar as number) && (p.fermetrar as number) > 0;
        const fastnum = p.fastanumerShort?.toString();

        const onVerdmatError = () => {
          if (!isCurrentPopup()) return;
          replaceVerdmat('<p class="text-sm text-muted mt-1 mb-1 italic">Ekki tókst að reikna verðmat</p>');
        };

        const onVerdmatBackupDone = (verdmat: any) => {
          if (!isCurrentPopup()) return;
          try {
            replaceVerdmat(`
            <div class="float-left w-100">
              <p class="!mt-1 !mb-1">
                Fermetraverð í hverfi: ${this.formatVerd((verdmat.estimatedValueSqm * 1000).toString())} kr./m²
              </p>
            </div>
          `);
          } catch {
            onVerdmatError();
          }
        };

        const onVerdmatDone = (verdmat: any) => {
          if (!isCurrentPopup()) return;
          try {
            const hasSqmEst = verdmat?.estimatedValueSqm != null && isFinite(verdmat.estimatedValueSqm);
            const styrkleiki = verdmat?.estimateStrength == 1 ? 'sterkt' : 'miðlungs';

            if (hasSqmEst && hasSqmInput) {
              replaceVerdmat(`
              <div class="float-left w-100">
                <p class="!mt-1 !mb-1">
                  Verðmat í dag (${styrkleiki}): ${this.formatToMillionKr(verdmat.estimatedValueSqm * (p.fermetrar as number) * 1000)}
                  <a href="${verdmatInfoUrl}" target="_blank" class="c-link">
                    <i class="fas fa-info-circle text-[12px] hover:cursor-pointer relative -top-px primary-color"
                       title="Upplýsingar um útreikning verðmats"></i>
                  </a>
                </p>
              </div>
            `);
            } else {
              this._fasteignService
                .estimatePropertyValueLatLong({ LatWgs84: p.latitude, LongWgs84: p.longitude })
                .subscribe({ next: onVerdmatBackupDone, error: onVerdmatError });
            }
          } catch {
            onVerdmatError();
          }
        };

        if (fastnum && hasSqmInput) {
          this._fasteignService
            .estimatePropertyValue(fastnum)
            .subscribe({ next: onVerdmatDone, error: onVerdmatError });
        } else {
          this._fasteignService
            .estimatePropertyValueLatLong({ LatWgs84: p.latitude, LongWgs84: p.longitude })
            .subscribe({ next: onVerdmatBackupDone, error: onVerdmatError });
        }

        // --- Sækja forsíðumynd og skipta inn (DOM-skipti, engin setContent) ---
        this._fasteignService
          .mblSaekjaProfilMynd({ MblFasteignId: p.mblFasteignId.toString() })
          .subscribe((forsiduMyndUrl: string) => {
            if (!isCurrentPopup() || !forsiduMyndUrl) return;

            // Víðtækara fallbackRegex til að þola whitespace/atribúta
            replaceInPopupBySelector(
              '.info-box-image-placeholder',
              `<img src="${forsiduMyndUrl}" alt="Ljósmynd" class="info-box-image" />`,
              /<div class="info-box-image-placeholder[^>]*>\s*<\/div>/
            );

            // Edge-tilvik: popup-element gæti hafa refreshast -> endurmerkjum
            setPopupToken();
          });

        // --- Fleiri en ein fasteign í húsi? (sams konar lógík og í Kaupsögu) ---
        const heimilisfangForMultiplicity = (p.heimilisfang ?? titleText ?? '').toString().trim();
        // Reiknum postnúmer varlega (MBL-línan getur heitið postnumer/postNumer eða vantað):
        const postnumerForMultiplicity = (p as any)?.postNumer ?? (p as any)?.postnumer ?? (kaup as any)?.postNumer ?? '';

        this.checkMoreThanOneProperty(heimilisfangForMultiplicity, postnumerForMultiplicity).subscribe({
          next: (hasMultiple: boolean) => {
            if (!isCurrentPopup()) return;
            if (hasMultiple) {
              const url = buildKaupsagaMultiplicityUrlPropertyUnique(
                (kaup as any)?.FasteignNr ?? null,
                heimilisfangForMultiplicity,
                postnumerForMultiplicity
              );

              const multiplicityHtml = `
              <span class="separator-line mt-3 mb-3 block"></span>
              <p class="mt-2 float-left w-100 text-[15px] text-gray-800 !mb-0">
                <a
                  href="${url}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-gray-800 hover:text-gray-900 italic"
                  aria-label="Skoða allar eignir í húsi"
                >
                  Skoða allar eignir í húsi
                </a>
              </p>
            `;

              replaceInPopupBySelector(
                '.property-multiplicity-placeholder',
                multiplicityHtml,
                /<div class="property-multiplicity-placeholder[^>]*><\/div>/
              );
            } else {
              // Fjarlægjum placeholder ef ekkert að sýna
              replaceInPopupBySelector(
                '.property-multiplicity-placeholder',
                '',
                /<div class="property-multiplicity-placeholder[^>]*><\/div>/
              );
            }
          },
          error: () => {
            if (!isCurrentPopup()) return;
            // Þögn á villu: fjarlægja placeholder
            replaceInPopupBySelector(
              '.property-multiplicity-placeholder',
              '',
              /<div class="property-multiplicity-placeholder[^>]*><\/div>/
            );
          }
        });
      });

      return marker;
    };

    //#endregion

    //#region Kaupsagan eign

    // Býr til pop-up fyrir fasteign skráða til sölu í Kaupsögunni (kaupsamningar)
    const makeKaupMarker = (p: any) => {
      const marker = this._leafletService.L.marker(
        [p.latWgs84, p.longWgs84],
        {
          icon: this.kaupsagaIcon(new Date(p.thinglystDags))
        }
      );

      marker.on('click', () => {
        this.triggerFullScreenOnPropertyClick(p.latWgs84, p.longWgs84);

        // --- Helpers: skipta út í DOM eða streng (fallback) ---
        const replaceInPopupBySelector = (selector: string, replacementHtml: string, fallbackRegex?: RegExp) => {
          const el = this.sharedPopup.getElement();
          if (el) {
            const node = el.querySelector(selector);
            if (node) {
              (node as HTMLElement).outerHTML = replacementHtml;
              return true;
            }
          }
          const current = this.sharedPopup.getContent() as any;
          if (typeof current === 'string' && fallbackRegex) {
            const updated = current.replace(fallbackRegex, replacementHtml);
            if (updated !== current) {
              this.sharedPopup.setContent(updated);
              return true;
            }
          }
          return false;
        };

        // --- EINKVÆÐ HJÁLP (FYRIR AUGLÝST VERÐ): finna "Kaupverð" <p> og setja HTML strax á eftir ---
        const insertAfterKaupverdParagraphPropertyUnique = (html: string) => {
          const el = this.sharedPopup.getElement() as (HTMLElement | null);

          // 1) DOM-leið: finna <p> sem byrjar á "Kaupverð (" og setja HTML á eftir
          if (el) {
            const nodeList = el.querySelectorAll('p');
            const paragraphs: HTMLElement[] = Array.from(nodeList) as HTMLElement[];

            const target = paragraphs.find((pNode: HTMLElement) => {
              const txt = (pNode.textContent ?? '').trim();
              return /^Kaupverð\s*\(/.test(txt);
            });

            if (target) {
              target.insertAdjacentHTML('afterend', html);
              return true;
            }
          }

          // 2) Fallback á streng: setja beint á eftir "Kaupverð (" <p> línunni
          const current = this.sharedPopup.getContent() as any;
          if (typeof current === 'string') {
            const kaupsverdRegex = /(<p[^>]*>\s*Kaupverð\s*\([^)]+\):\s*[^<]*<\/p>)/i;
            const updated = current.replace(kaupsverdRegex, `$1\n\n${html}`);
            if (updated !== current) {
              this.sharedPopup.setContent(updated);
              return true;
            }
          }

          return false;
        };

        // --- Dagsetningar- og HTML-hjálp (EINKVÆÐ) fyrir "Auglýst verð" ---
        const formatPropertyAdDateDdMmYyyyUnique = (value: string | Date | null | undefined): string => {
          if (!value) return '—';
          const d = (value instanceof Date) ? value : new Date(value);
          if (isNaN(d.getTime())) return '—';
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const yyyy = String(d.getFullYear());
          return `${dd}.${mm}.${yyyy}`;
        };

        const buildPropertyAdvertisedPriceHtmlUnique = (priceKr: number | null, dateVal: string | Date | null | undefined): string => {
          const dateStr = formatPropertyAdDateDdMmYyyyUnique(dateVal);
          const priceStr = (priceKr != null && priceKr > 0) ? this.formatToMillionKr(priceKr) : '—';
          return `
          <p class="!mt-1 !mb-1">
            Auglýst verð (${dateStr}): ${priceStr}
          </p>
        `;
        };

        // --- Finna samsvarandi MBL færsla (auglýsingagögn) með fastanúmeri ---
        const mblProperty: ListMblFasteignRes | undefined =
          this.nearbyFasteignirMbl?.find((x: any) => norm(x.fastanumerShort) === norm(p.fastnum));

        // --- Velja fasteignamat: fyrst úr kaupsögu (p), annars úr MBL ef til ---
        const matFromKaupsaga = (p as any)?.fasteignamatGildandi;
        const matFromMbl = (mblProperty as any)?.fasteignamat;
        let chosenMat: number | null =
          (matFromKaupsaga != null && isFinite(matFromKaupsaga)) ? Number(matFromKaupsaga)
            : (matFromMbl != null && isFinite(matFromMbl)) ? Number(matFromMbl)
              : null;

        // Ef talið er í þúsundum -> normalísera í krónur
        if (chosenMat != null && chosenMat > 0 && chosenMat < 1_000_000) {
          chosenMat = chosenMat * 1000;
        }

        const fasteignamatHtml = `
            <p class="!mt-1 !mb-1">
              Fasteignamat í dag: ${chosenMat != null && chosenMat > 0 ? this.formatToMillionKr(chosenMat) : '—'}
            </p>
          `;

        // --- Textar fyrir kaupverð og dagsetningar (úr kaupsögu) ---
        const kaupverdStr = (p.kaupverd != null && p.kaupverd !== 0)
          ? this.formatToMillionKr((p.kaupverd * 1000) as number)
          : '—';
        const kaupdagurStr = this.formatDate(new Date(p.thinglystDags));

        // --- Linkar á einum stað (alltaf vísa á kaupsöguna) ---
        const kaupsagaUrl = `${this._environment.rootPublicUrl}/kaupsagan/${norm(p.fastnum)}`;

        // --- Titill: "Heimilisfang, 123 m²" – stólum á kaupsögu; fallback í MBL ef einflm vantar ---
        const sqmFromKaupsaga = (p?.einflm != null && isFinite(p.einflm)) ? Number(p.einflm) : null;
        const sqmFinal = sqmFromKaupsaga ?? (mblProperty?.fermetrar ?? null);
        const titleText = `${p.heimilisfang}${sqmFinal ? `, ${sqmFinal} m²` : ''}`;

        // --- Info-hluti undir separator: Fasteignamat + Verðmat placeholder ---
        const detailHtml = `
      <div class="float-left w-100">
        ${fasteignamatHtml}
        <div class="verdmat-placeholder text-sm text-muted italic">Reikna verðmat…</div>
      </div>
      <span class="separator-line"></span>
    `;

        this.ensureLeafletPopupButtonStyles();

        const multiplicityPlaceholder = `
          <div class="property-multiplicity-placeholder">
          </div>
        `;

        const baseContent = `
      <div class="leaflet-popup-content min-w-[275px]">
        <div class="info-box-content relative">
          <a href="${kaupsagaUrl}" target="_blank" rel="noopener noreferrer">
            <div class="info-box-image-placeholder"></div>
          </a>
  
          <h2 class="float-left w-100 !mb-1 !mt-2">${titleText}</h2>
  
          <p class="!mt-1 !mb-1">Kaupverð (${kaupdagurStr}): ${kaupverdStr}</p>
  
          <span class="separator-line"></span>
          ${detailHtml}
  
          <div class="float-left w-100 mt-2 flex flex-wrap items-end gap-2">
            <a
              class="orange-btn orange-btn--lg"
              href="${kaupsagaUrl}"
              target="_blank"
              rel="noopener noreferrer"
              role="button"
              aria-label="Skoða fasteign"
            >
              Skoða fasteign
            </a>
  
            <a
              href="${kaupsagaUrl}"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Skoða kaupsögu"
              class="ml-auto"
            >
              <img
                src="/assets/images/icons/new/logo-sm.svg"
                alt="kaupsaga"
                class="h-6 w-auto self-end"
              />
            </a>
          </div>
  
          ${multiplicityPlaceholder}
        </div>
      </div>
    `;

        this.sharedPopup
          .setLatLng(marker.getLatLng())
          .setContent(baseContent)
          .openOn(this.map);

        // --- Verðmat: estimatePropertyValue(fastnum) * m² (forgangur: kaupsaga m²; fallback: MBL m²); backup með Lat/Long ---
        const sqmForEstimate = (sqmFromKaupsaga ?? mblProperty?.fermetrar) as number | undefined;
        const fastnum = p.fastnum ? String(p.fastnum) : null;

        let htmlVerdmat = '<p class="text-sm text-muted mt-1 mb-1 italic">Ekki tókst að reikna verðmat</p>';

        const replaceVerdmat = (html: string) => {
          return replaceInPopupBySelector(
            '.verdmat-placeholder',
            html,
            /<div class="verdmat-placeholder[^>]*">[\s\S]*?<\/div>/
          );
        };

        const onVerdmatBackupDone = (verdmat: any) => {
          try {
            if (verdmat?.estimatedValueSqm != null && isFinite(verdmat.estimatedValueSqm)) {
              htmlVerdmat = `
            <div class="float-left w-100">
              <p class="!mt-1 !mb-1">
                Fermetraverð í hverfi: ${this.formatVerd((verdmat.estimatedValueSqm * 1000).toString())} kr./m²
              </p>
            </div>
          `;
            }
            replaceVerdmat(htmlVerdmat);
          } catch {
            replaceVerdmat(htmlVerdmat);
          }
        };

        const onVerdmatError = () => {
          replaceVerdmat(htmlVerdmat);
        };

        const onVerdmatDone = (verdmat: any) => {
          try {
            const styrkleiki = verdmat?.estimateStrength == 1 ? 'sterkt' : 'miðlungs';
            htmlVerdmat = `
          <div class="float-left w-100">
            <p class="!mt-1 !mb-1">
              Verðmat í dag (${styrkleiki}): ${this.formatToMillionKr(verdmat.estimatedValueSqm * (sqmForEstimate as number) * 1000)}
              <a href="${verdmatInfoUrl}" target="_blank" class="c-link">
                <i class="fas fa-info-circle text-[12px] hover:cursor-pointer relative -top-px primary-color" title="Upplýsingar um útreikning verðmats"></i>
              </a>
            </p>
          </div>
        `;
            replaceVerdmat(htmlVerdmat);
          } catch {
            this._fasteignService
              .estimatePropertyValueLatLong({ LatWgs84: p.latWgs84, LongWgs84: p.longWgs84 })
              .subscribe({ next: onVerdmatBackupDone, error: onVerdmatError });
          }
        };

        if (fastnum) {
          this._fasteignService.estimatePropertyValue(fastnum).subscribe({ next: onVerdmatDone, error: onVerdmatError });
        } else {
          this._fasteignService
            .estimatePropertyValueLatLong({ LatWgs84: p.latWgs84, LongWgs84: p.longWgs84 })
            .subscribe({ next: onVerdmatBackupDone, error: onVerdmatError });
        }

        // --- Sækja forsíðumynd (úr kaupsögu þjónustu) og skipta inn ---
        this._fasteignService.kaupskraSaekjaProfilMynd({
          Fastnum: norm(p.fastnum),
          Lat: p.latWgs84,
          Lng: p.longWgs84,
          Postnumer: p.postNumer
        }).subscribe((forsiduMyndUrl: string) => {
          if (!forsiduMyndUrl) return;
          replaceInPopupBySelector(
            '.info-box-image-placeholder',
            `<img src="${forsiduMyndUrl}" alt="Ljósmynd" class="info-box-image" />`,
            /<div class="info-box-image-placeholder"><\/div>/
          );
        });

        // --- Fleiri en ein fasteign í húsi? Setja tengil ef true, annars fjarlægja placeholder ---
        const heimilisfangForMultiplicity = (p.heimilisfang ?? titleText ?? '').toString().trim();

        this.checkMoreThanOneProperty(heimilisfangForMultiplicity, p.postNumer).subscribe({
          next: (hasMultiple: boolean) => {
            if (hasMultiple) {
              const heimilisfangEnc = encodeURIComponent(heimilisfangForMultiplicity ?? '');
              const postnumerEnc = encodeURIComponent(p.postNumer ?? '');
              const kaupsagaMultipleUrl = `${this._environment.rootPublicUrl}/kaupsagan/eignalisti?address=${heimilisfangEnc}&postal=${postnumerEnc}`;

              const multiplicityHtml = `
                  <span class="separator-line mt-3 mb-3 block"></span>
              <p class="mt-2 float-left w-100 text-[15px] text-gray-800 !mb-0">
                <a
                  href="${kaupsagaMultipleUrl}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-gray-800 hover:text-gray-900 italic"
                  aria-label="Skoða allar eignir í húsi"
                >
                  Skoða allar eignir í húsi
                </a>
              </p>
                `;

              replaceInPopupBySelector(
                '.property-multiplicity-placeholder',
                multiplicityHtml,
                /<div class="property-multiplicity-placeholder[^>]*"><\/div>/
              );
            } else {
              // Fjarlægja placeholder ef ekkert að sýna
              replaceInPopupBySelector(
                '.property-multiplicity-placeholder',
                '',
                /<div class="property-multiplicity-placeholder[^>]*"><\/div>/
              );
            }
          },
          error: () => {
            // Þögn á villu: fjarlægja placeholder
            replaceInPopupBySelector(
              '.property-multiplicity-placeholder',
              '',
              /<div class="property-multiplicity-placeholder[^>]*"><\/div>/
            );
          }
        });

        // --- Sækja "Auglýst verð" (ENGIN BIRTING FYRR EN TILBÚIÐ; ef ekkert finnst þá sleppum við) ---
        try {
          this._fasteignService
            .readAuglysingasagaLatest(String(p.fastnum))
            .subscribe({
              next: (auglysingasaga: { verd?: number | null; dagsetningSkraningar?: string | Date | null }) => {
                try {
                  let verdKr: number | null = (auglysingasaga?.verd != null && isFinite(auglysingasaga.verd))
                    ? Number(auglysingasaga.verd)
                    : null;

                  // Ef virðist í þúsundum -> normalísera í krónur
                  if (verdKr != null && verdKr > 0 && verdKr < 1_000_000) {
                    verdKr = verdKr * 1000;
                  }

                  // Ef ekkert verð eða 0 -> birta ekki neitt
                  if (!(verdKr != null && verdKr > 0)) {
                    return;
                  }

                  const html = buildPropertyAdvertisedPriceHtmlUnique(verdKr, auglysingasaga?.dagsetningSkraningar ?? null);

                  // Setjum línuna strax á eftir "Kaupverð (…)” <p>
                  insertAfterKaupverdParagraphPropertyUnique(html);
                } catch {
                  // þögn á villu: sleppa birtingu
                }
              },
              error: () => {
                // þögn á villu: sleppa birtingu
              }
            });
        } catch {
          // þögn á villu: sleppa birtingu
        }
      });

      return marker;
    };

    //#endregion

    //#region Grunneign 

    const openGrunneignPropertyPopup = (p: ReadPublicKaupskraThjodskraRes, latlng: any) => {

      this.triggerFullScreenOnPropertyClick(latlng.lat, latlng.lng);

      // --- Linkar á einum stað (alltaf vísa á kaupsöguna) ---
      const norm = (x: any) => x; // <- notaðu þitt eigið norm
      const kaupsagaUrl = `${this._environment.rootPublicUrl}/kaupsagan/${norm(p.fastnum)}`;
      const titleKaupsagaText = p.heimilisfang;

      // --- Info-hluti: Verðmat placeholder ---
      const detailKaupsagaHtml = `
      <div class="float-left w-100">
        <div class="verdmat-placeholder text-sm text-muted italic">Reikna verðmat…</div>
      </div>
      <span class="separator-line"></span>
    `;

      this.ensureLeafletPopupButtonStyles();

      // Mikilvægt: fastar víddir á placeholder til að koma í veg fyrir layout-hopp
      const baseGrunneignContent = `
      <div class="leaflet-popup-content min-w-[275px]">
        <div class="info-box-content relative">
          <a href="${kaupsagaUrl}" target="_blank" rel="noopener noreferrer">
            <div class="info-box-image-placeholder" style="width:360px;height:240px;"></div>
          </a>
          <h2 class="float-left w-100 !mb-1 !mt-4">${titleKaupsagaText}</h2>
          ${detailKaupsagaHtml}
          <div class="float-left w-100 mt-2 flex flex-wrap gap-2">
            <a class="orange-btn orange-btn--lg" href="${kaupsagaUrl}" target="_blank" rel="noopener noreferrer" role="button" aria-label="Skoða fasteign">
              Skoða fasteign
            </a>
          </div>
  
          <!-- Placeholder fyrir "fleiri en ein eign í húsi" skilaboð -->
          <div class="property-multiplicity-placeholder"></div>
        </div>
      </div>
    `;

      // Opna strax með grunn-HTML (engin eftirfylgni setContent í async)
      this.sharedPopup
        .setLatLng(latlng)
        .setContent(baseGrunneignContent)
        .openOn(this.map);

      // --- Popup guard: aðeins uppfæra ef þetta popup er enn opið
      const popupToken = `${Date.now()}-${Math.random()}`;
      const setPopupToken = () => {
        const el = this.sharedPopup.getElement();
        if (el) el.setAttribute('data-popup-token', popupToken);
      };
      const isCurrentPopup = () =>
        this.sharedPopup.getElement()?.getAttribute('data-popup-token') === popupToken;

      setPopupToken();

      // --- DOM-only helper: engin strengja-fallback, engin setContent í async
      const replaceDom = (selector: string, replacementHtml: string) => {
        const el = this.sharedPopup.getElement();
        if (!el) {
          requestAnimationFrame(() => replaceDom(selector, replacementHtml));
          return;
        }
        const node = el.querySelector(selector);
        if (!node) return;
        const n = node as HTMLElement;
        if (n.outerHTML === replacementHtml) return; // idempotent
        n.outerHTML = replacementHtml;
      };

      // --- Idempotent innsetningar (einu sinni)
      const insertCoverOnce = (url: string) => {
        if (!isCurrentPopup()) return;
        const el = this.sharedPopup.getElement();
        if (!el) return;

        // Ef mynd er þegar komin, ekki gera neitt
        if (el.querySelector('.info-box-image')) return;

        // Preload áður en skipt er inn
        const img = new Image();
        (img as any).decoding = 'async';
        img.loading = 'eager';
        img.width = 360;
        img.height = 240;
        img.alt = 'Ljósmynd';
        img.className = 'info-box-image';
        img.onload = () => {
          if (!isCurrentPopup()) return;
          replaceDom(
            '.info-box-image-placeholder',
            `<img src="${url}" alt="Ljósmynd" class="info-box-image" width="360" height="240" loading="eager" />`
          );
          setPopupToken(); // ef Leaflet “refreshar” content
        };
        img.onerror = () => { /* halda placeholder */ };
        img.src = url;
      };

      const renderVerdmatOnce = (innerHtml: string) => {
        if (!isCurrentPopup()) return;
        const el = this.sharedPopup.getElement();
        if (!el) return;
        if (el.querySelector('.verdmat-result')) return; // þegar sett
        const node = el.querySelector('.verdmat-placeholder');
        if (!node) return;
        (node as HTMLElement).outerHTML = `<div class="verdmat-result float-left w-100">${innerHtml}</div>`;
      };

      // --- NÝTT: Multiplicity helpers (einkvæð nöfn með "property") ---
      const buildPropertyHouseAllUrlGrunneignUnique = (heimilisfang: string, postnumer: string) => {
        const heimilisfangEnc = encodeURIComponent((heimilisfang ?? '').toString().trim());
        const postnumerEnc = encodeURIComponent((postnumer ?? '').toString().trim());
        // Sama mynstrið og í hinu: vísa á eignalista með query params fyrir address & postal
        return `${this._environment.rootPublicUrl}/kaupsagan/eignalisti?address=${heimilisfangEnc}&postal=${postnumerEnc}`;
      };

      const renderPropertyMultiplicityOnceGrunneignUnique = (innerHtml: string) => {
        if (!isCurrentPopup()) return;
        const el = this.sharedPopup.getElement();
        if (!el) return;
        const node = el.querySelector('.property-multiplicity-placeholder');
        if (!node) return;
        (node as HTMLElement).outerHTML = innerHtml; // idempotent að því leyti að við skrifum einu sinni á placeholder
      };

      const startPropertyMultiplicityCheckGrunneignUnique = () => {
        // Reynum að finna sanngjarnt heimilisfang + póstnúmer
        const heimilisfang = (p as any).Heimilisfang ?? titleKaupsagaText ?? '';
        const postnumer = (p as any).PostNr ?? '';
        const fasteignNr = p.fastnum;

        // Ef við höfum ekki gögn, fjarlægjum einfaldlega placeholder
        if (!heimilisfang || !postnumer) {
          renderPropertyMultiplicityOnceGrunneignUnique('');
          return;
        }

        // Kalla á sömu þjónustu og í fyrri kóða (Observable<boolean>)
        this.checkMoreThanOneProperty(heimilisfang.toString().trim(), postnumer.toString().trim()).subscribe({
          next: (hasMultiple: boolean) => {
            if (!isCurrentPopup()) return;

            if (hasMultiple) {
              const allUrl = buildPropertyHouseAllUrlGrunneignUnique(heimilisfang, postnumer);
              const multiplicityHtml = `
                  <span class="separator-line mt-3 mb-3 block"></span>
                  <p class="mt-2 float-left w-100 text-[15px] text-gray-800 !mb-0">
                    <a
                      href="${allUrl}"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="underline text-gray-800 hover:text-gray-900 italic"
                      aria-label="Skoða allar eignir í húsi"
                    >
                      Skoða allar eignir í húsi
                    </a>
                  </p>
                `;

              renderPropertyMultiplicityOnceGrunneignUnique(multiplicityHtml);
            } else {
              // Fjarlægja placeholder ef ekkert að sýna
              renderPropertyMultiplicityOnceGrunneignUnique('');
            }
          },
          error: () => {
            // Þögn á villu: fjarlægja placeholder
            renderPropertyMultiplicityOnceGrunneignUnique('');
          }
        });
      };

      // --- Verðmat (async, Lat/Long) — DOM-only uppfærsla
      const verdmatErrorHtml = `<p class="text-sm text-muted mt-1 mb-1 italic">Ekki tókst að reikna verðmat</p>`;

      const onVerdmatKaupsagaError = () => {
        if (!isCurrentPopup()) return;
        renderVerdmatOnce(verdmatErrorHtml);
      };

      const onVerdmatKaupsagaBackupDone = (verdmat: any) => {
        if (!isCurrentPopup()) return;
        try {
          renderVerdmatOnce(`
          <p class="!mt-1 !mb-1">
            Fermetraverð í hverfi: ${this.formatVerd((verdmat.estimatedValueSqm * 1000).toString())} kr./m²
          </p>
        `);
        } catch {
          onVerdmatKaupsagaError();
        }
      };

      this._fasteignService
        .estimatePropertyValueLatLong({ LatWgs84: p.latWgs84, LongWgs84: p.longWgs84 })
        .subscribe({ next: onVerdmatKaupsagaBackupDone, error: onVerdmatKaupsagaError });

      // --- Forsíðumynd (async) — preload + DOM-only skipti, föst vídd
      this._fasteignService.kaupskraSaekjaProfilMynd({
        Fastnum: norm(p.fastnum),
        Lat: p.latWgs84,
        Lng: p.longWgs84,
        Postnumer: p.postNumer
      }).subscribe((forsiduMyndUrl: string) => {
        if (!isCurrentPopup() || !forsiduMyndUrl) return;
        insertCoverOnce(forsiduMyndUrl);
      });

      // --- NÝTT: ræsa leit á "fleiri en ein eign í húsi" (async, DOM-only) ---
      startPropertyMultiplicityCheckGrunneignUnique();
    };

    const makeGrunneignMarker = (p: ReadPublicKaupskraThjodskraRes) => {
      const marker = this._leafletService.L.marker([p.latWgs84, p.longWgs84], {
        icon: this.kaupsagaIcon(new Date(1900, 1))
      });

      // 2) Sýna sama HTML popup þegar notandi smellir
      marker.on('click', () => {
        openGrunneignPropertyPopup(p, marker.getLatLng());
      });

      return marker;
    };

    //#endregion

    const desiredFastnums = new Set<string>();
    for (const k of efastByFn.keys()) desiredFastnums.add(k);
    for (const k of mblByFn.keys()) desiredFastnums.add(k);
    for (const k of kaupByFn.keys()) desiredFastnums.add(k);
    for (const k of grunnEignFn.keys()) desiredFastnums.add(k);

    // --- Ákvarða óskastöðu fyrir hvert fastanúmer m.t.t. forgangs ---
    type Desired = { source: 'efast' | 'mbl' | 'kaup' | 'grunn'; lat: number; lng: number; build: () => any };

    const desiredByFn = new Map<string, Desired>();

    for (const fn of desiredFastnums) {
      if (efastByFn.has(fn)) {
        const p = efastByFn.get(fn)!;
        desiredByFn.set(fn, { source: 'efast', lat: p.laT_WGS84, lng: p.lonG_WGS84, build: () => makeEfastMarker(p) });
        continue;
      }
      if (mblByFn.has(fn)) {
        const p = mblByFn.get(fn)!;
        desiredByFn.set(fn, { source: 'mbl', lat: p.latitude, lng: p.longitude, build: () => makeMblMarker(p) });
        continue;
      }
      if (kaupByFn.has(fn)) {
        const p = kaupByFn.get(fn)!;
        desiredByFn.set(fn, { source: 'kaup', lat: p.latWgs84, lng: p.longWgs84, build: () => makeKaupMarker(p) });
        continue;
      }
      const p = grunnEignFn.get(fn)!;
      desiredByFn.set(fn, { source: 'grunn', lat: p.latWgs84, lng: p.longWgs84, build: () => makeGrunneignMarker(p) });

      continue;
      // ef hvorugt hittast (t.d. síur breyttar), sleppum fn
    }

    // --- Reconcile: fjarlægja óþarfa, bæta við nýjum, skipta út ef þarf ---
    const toAdd: any[] = [];
    const toRemove: any[] = [];

    // Henda út grunneignar-merkjum ef til er "alvöru" færsla (efast/mbl/kaup) fyrir sama fastanúmer
    for (const [fn, rec] of this.fastanumToMarker.entries()) {
      if (rec.source === 'grunn' && (efastByFn.has(fn) || mblByFn.has(fn) || kaupByFn.has(fn))) {
        toRemove.push(rec.layer);
        this.fastanumToMarker.delete(fn);
      }
    }

    // 1) Fjarlægja merki sem eru ekki lengur í desired (t.d. datt út úr view/síum)
    for (const [fn, rec] of this.fastanumToMarker.entries()) {
      if (!desiredByFn.has(fn)) {
        toRemove.push(rec.layer);
        this.fastanumToMarker.delete(fn);
      }
    }

    // 2) Bæta við/uppfæra þau sem eiga að sjást
    for (const [fn, want] of desiredByFn.entries()) {
      const cur = this.fastanumToMarker.get(fn);
      if (cur) {
        // Ef source og staðsetning eru óbreytt — ekki gera neitt (idempotent).
        if (cur.source === want.source && samePos(cur.lat, cur.lng, want.lat, want.lng)) {
          continue;
        }
        // Annars skiptum út
        toRemove.push(cur.layer);
        const newLayer = want.build();
        toAdd.push(newLayer);
        this.fastanumToMarker.set(fn, { layer: newLayer, source: want.source, lat: want.lat, lng: want.lng });
      } else {
        // Nýtt merki
        const layer = want.build();
        toAdd.push(layer);
        this.fastanumToMarker.set(fn, { layer, source: want.source, lat: want.lat, lng: want.lng });
      }
    }

    // Uppfæruim aðeins clusterGroups ef það varð breyting á því sem við erum með í global og því sem kemur út úr nýjustu síun.
    this.applyPropertyFastnumClusterDiffSafely_unique(this.clusterGroup, toAdd, toRemove, desiredFastnums, this);
  }

  checkMoreThanOneProperty(heimilisfang: string, postnumer: number): Observable<boolean> {
    const httpReq: ExposedListPublicKaupskraThjodskraHmsSearchReq = {
      pageNumber: 1,
      pageSize: 25,
      searchText: heimilisfang,
      region: [postnumer],
      order: "Address",
      strictSearch: true,
      addDbEntries: false
    };

    // búum til regex sem tryggir að það passi aðeins við heildarheimilisfang
    // notum \b fyrir orðamörk og ^ / $ fyrir byrjun og endi ef það á við
    const regex = new RegExp(`^${heimilisfang.trim()}$`, 'i');

    return this._fasteignService.listPublicKaupskraThjodskraHmsSearch(httpReq).pipe(
      map(hmsEignirRes => {
        const filtered = hmsEignirRes.filter(
          x => regex.test(x.Birtingarmynd?.trim() ?? '')
        );

        // skila true ef fleiri en ein nákvæmlega samsvarandi eign fannst
        return filtered.length > 1;
      }),
      finalize(() => {
        // ef þú vilt hreinsa state eða logga aðgerð, bættu því hér
      })
    );
  }

  /**
 * Uppfærir clusterGroup út frá nýjum fasteignanúmerum (property fastnums)
 * á öruggari hátt: borðar saman innihald setta, ver gegn null/undefined,
 * og forðar óþarfa endurvinnslu.
 */
  applyPropertyFastnumClusterDiffSafely_unique(
    clusterGroup: any,
    toAdd: any[] | null | undefined,
    toRemove: any[] | null | undefined,
    desiredFastnums: Set<string | number> | null | undefined,
    ctx: { desiredFastnums?: Set<string | number> } // t.d. `this`
  ): void {
    // Vörn gegn rangri notkun
    if (!clusterGroup) return;
    const addList = Array.isArray(toAdd) ? toAdd : [];
    const removeList = Array.isArray(toRemove) ? toRemove : [];
    const nextSet = desiredFastnums ?? new Set<string | number>();

    const prevSet = ctx.desiredFastnums ?? new Set<string | number>();

    // Djúp jöfnun á innihaldi setta (ekki bara size)
    if (prevSet.size === nextSet.size) {
      let equal = true;
      for (const v of nextSet) {
        if (!prevSet.has(v)) { equal = false; break; }
      }
      if (equal) {
        // Engin raunveruleg breyting á "desired" → sleppum vinnu
        return;
      }
    }

    // Framkvæmum atómískt eins og hægt er: fyrst fjarlægja, svo bæta við.
    // Leaflet MarkerClusterGroup styður bulk aðgerðir.
    try {
      if (removeList.length) clusterGroup.removeLayers(removeList);
      if (addList.length) clusterGroup.addLayers(addList);

      // Aðeins uppfæra stöðuna ef við náðum í gegn án villu
      ctx.desiredFastnums = new Set(nextSet);
    } catch (err) {
      // Ef eitthvað bilar, reynum að rúlla hluta til baka (best effort)
      try {
        if (addList.length) clusterGroup.removeLayers(addList);
        if (removeList.length) clusterGroup.addLayers(removeList);
      } catch { /* ignóra endurheimtuvillu */ }
      throw err; // Láta ytra lag sjá villuna ef þú vilt logga
    }
  }

  // ===== VIEWPORT-BOUND RENDER =====
  kaupsagaIcon(date: Date) {
    const iconFile = this.isWithinOneYear(date)
      ? 'sold-recent.png'
      : 'sold.png';

    return this._leafletService.L.icon({
      iconUrl: this._environment.rootPublicUrl + '/assets/images/others/markers/new/' + iconFile,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });
  }

  //#endregion

  //#region -- Recenter

  private recenterToMain() {
    const ll = this.mainMarker?.getLatLng?.() ?? this.mapCenter;
    if (ll) this.centerMapOn(ll, { animate: true, zoom: 15 });
  }

  private addRecenterControl(map: any) {
    const L = this._leafletService.L;

    const RecenterControl = L.Control.extend({
      options: { position: 'topright' },
      onAdd: () => {
        const wrap = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom recenter-btn leaflet-bar-center');
        const btn = L.DomUtil.create('a', '', wrap);

        btn.href = '#';
        btn.title = 'Miðja kort';
        btn.setAttribute('role', 'button');
        btn.setAttribute('aria-label', 'Miðja kort');
        btn.setAttribute('tabindex', '0');
        btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                width="20" height="20"
                fill="none" 
                stroke="currentColor" 
                stroke-width="1.8" 
                stroke-linecap="round" 
                stroke-linejoin="round">
              <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21"/>
              <circle cx="12" cy="12" r="6.5"/>
              <circle cx="12" cy="12" r="1.6" fill="currentColor"/>
            </svg>
          `;

        // koma í veg fyrir map-drag & default jump
        L.DomEvent.on(btn, 'click', L.DomEvent.stopPropagation)
          .on(btn, 'click', L.DomEvent.preventDefault)
          .on(btn, 'click', () => this.recenterToMain())
          .on(btn, 'keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.recenterToMain();
            }
          });

        return wrap;
      }
    });

    map.addControl(new RecenterControl());
  }

  //#endregion

  //#region -- Fullscreen

  // Hjálparaðgerð sem tryggir að við förum úr full-screen stillingu á síðu, t.d. í Facebook-appi,
  // ef slóð með target="_blank" er valin á korti þegar við erum í full-screen. Annars lokast kortið ekki þegar við smellum.
  private watchExternalLinkClick() {
    this._externalLinkWatcher
      .initExternalLinkWatcher()
      .subscribe((event) => {
        if (this.isInFs()) {
          this.toggleFullscreen();
        }
      });
  }

  private readonly isIosFullscreenEnvironment: boolean =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

  // State: er kortið okkar í fullscreen (native eða pseudo-iOS)?
  private propertyFullscreenActive = false;

  // Hjálparfall til að velja map í DOM.
  private get mapEl(): HTMLElement | null {
    return document.getElementById('leafletMapSold');
  }

  // Bætir við controll fyrir fullscreen.
  private addFullscreenControl(mapInstance: any) {
    const FullscreenControl = this._leafletService.L.Control.extend({
      options: {
        position: 'topright' // hægra megin uppi
      },

      onAdd: (_map: any) => {
        const container = this._leafletService.L.DomUtil.create(
          'div',
          'leaflet-bar leaflet-control leaflet-control-custom fullscreen-btn'
        );

        container.innerHTML = '⛶'; // bara ikon
        container.title = 'Stækka / Minnka kort';

        // koma í veg fyrir að kortið draggi þegar smellt er á takkann
        this._leafletService.L.DomEvent.disableClickPropagation(container);

        container.onclick = () => {
          this.toggleFullscreen();
        };

        return container;
      }
    });

    mapInstance.addControl(new FullscreenControl());
  }

  // Cross-browser helpers (Safari / iOS pseudo fullscreen)
  private requestFs(mapContainerElement: HTMLElement): void {
    if (this.isIosFullscreenEnvironment) {
      // pseudo fullscreen á iOS
      mapContainerElement.classList.add('leaflet-map-fullscreen');
      document.body.classList.add('leaflet-body-fullscreen');

      const backToTopElement = document.querySelector('.back-to-top') as HTMLElement | null;
      if (backToTopElement) {
        backToTopElement.style.display = 'none';
      }

      this.propertyFullscreenActive = true;

      setTimeout(() => {
        this.map?.invalidateSize();
        this.updatePropertyMapFullscreenCloseVisibility();
      }, 0);

      return;
    }

    // venjulegt Fullscreen API á öðrum browseurum
    const fullscreenElementAny = mapContainerElement as any;
    const fullscreenRequestMethod =
      fullscreenElementAny.requestFullscreen ||
      fullscreenElementAny.webkitRequestFullscreen ||
      fullscreenElementAny.msRequestFullscreen ||
      fullscreenElementAny.mozRequestFullScreen;

    if (fullscreenRequestMethod) {
      fullscreenRequestMethod.call(mapContainerElement);
      // við stillum state hér, en fullscreenchange handler á desktop
      // mun samt yfirskrifa þetta ef eitthvað fer úrskeiðis
      this.propertyFullscreenActive = true;
      this.updatePropertyMapFullscreenCloseVisibility();
    }
  }

  // Loka fullscreen
  private exitFs(): void {
    if (this.isIosFullscreenEnvironment) {
      const container = this.map?.getContainer();
      if (container) {
        container.classList.remove('leaflet-map-fullscreen');
      }

      document.body.classList.remove('leaflet-body-fullscreen');

      const backToTopElement = document.querySelector('.back-to-top') as HTMLElement | null;
      if (backToTopElement) {
        backToTopElement.style.display = 'flex';
      }

      this.propertyFullscreenActive = false;

      setTimeout(() => {
        this.map?.invalidateSize();
        this.updatePropertyMapFullscreenCloseVisibility();
      }, 0);

      return;
    }

    const docAny = document as any;
    const exitMethod =
      document.exitFullscreen ||
      docAny.webkitExitFullscreen ||
      docAny.msExitFullscreen ||
      docAny.mozCancelFullScreen;

    if (exitMethod) {
      exitMethod.call(document);
    }

    // fullscreenchange handler mun líka keyra, en við uppfærum state
    // hér til öryggis ef event týnist
    this.propertyFullscreenActive = false;
    this.updatePropertyMapFullscreenCloseVisibility();
  }

  // Segir til um hvort þú sért í fullscreen eða ekki (byggir á state)
  private isInFs(): boolean {
    return this.propertyFullscreenActive;
  }

  // Triggerar fullscreen takkann.
  async toggleFullscreen() {
    const el = this.mapEl;
    if (!el) return;

    try {
      if (!this.isInFs()) {
        await this.requestFs(el);
      } else {
        await this.exitFs();
      }
    } finally {
      // Smá delay svo browser klári layout áður en Leaflet reiknar stærð
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 50);
    }
  }

  // Hjálparfall til að fylgjast með native fullscreen (ekki iOS pseudo)
  monitorFullScreen() {
    const handler = () => {
      setTimeout(() => {
        if (!this.map) return;

        this.map.invalidateSize();

        const docAny = document as any;
        const fsElement =
          document.fullscreenElement ||
          docAny.webkitFullscreenElement ||
          docAny.mozFullScreenElement ||
          docAny.msFullscreenElement;

        const mapContainer = this.map.getContainer();
        const isMapFullScreen =
          !!fsElement && (fsElement === mapContainer || fsElement.contains(mapContainer));

        // Uppfæra zoom behavior
        if (isMapFullScreen) {
          this.map.scrollWheelZoom.enable();
        } else {
          this.map.scrollWheelZoom.disable();
        }

        // Uppfæra fullscreen state + "Loka glugga" á desktop
        if (!this.isIosFullscreenEnvironment) {
          this.propertyFullscreenActive = isMapFullScreen;
          this.updatePropertyMapFullscreenCloseVisibility();
        }
      }, 50);
    };

    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler as any);
  }

  // Föllinn hér að neðan eru hjálparföll þegar notandinn velur eign á korti þá viljum við triggera fullscreen í hvert skipti.
  private propertyFullscreenCloseContainer?: HTMLElement;
  private propertyFullscreenCloseListenerAttached = false;

  async triggerFullScreenOnPropertyClick(lat: number, lng: number, zoom?: number) {
    const el = this.mapEl;

    if (!el || !this.map) return;

    try {
      // Vertu alltaf viss um að við förum í fullscreen, ekki toggle
      if (!this.isInFs()) {
        await this.requestFs(el);
      }
    } finally {
      setTimeout(() => {
        if (!this.map) return;

        this.map.invalidateSize();

        const currentZoom = this.map.getZoom();
        const adjustedLatLng = this.offsetCenter(lat, lng, 150);

        this.map.setView(adjustedLatLng, zoom ?? currentZoom);
      }, 50);
    }
  }

  private offsetCenter(lat: number, lng: number, offsetY: number) {
    const point = this.map.latLngToContainerPoint([lat, lng]);
    const newPoint = this._leafletService.L.point(point.x, point.y - offsetY);
    return this.map.containerPointToLatLng(newPoint);
  }

  private addFullscreenCloseControl(mapInstance: any): void {
    const controlPosition = this.isIosFullscreenEnvironment ? 'topleft' : 'bottomright';

    const FullscreenCloseControl = this._leafletService.L.Control.extend({
      options: {
        position: controlPosition
      },

      onAdd: (_map: any) => {
        const container = this._leafletService.L.DomUtil.create(
          'div',
          'leaflet-bar leaflet-control leaflet-control-custom leaflet-control-fullscreen-close'
        );

        container.innerHTML = `
        <div class="map-fs-close-btn flex items-center gap-2">
          <i class="fa-solid fa-compress"></i>
          <span>Loka glugga</span>
        </div>
      `;

        this._leafletService.L.DomEvent.disableClickPropagation(container);

        container.onclick = () => {
          if (this.isInFs()) {
            this.toggleFullscreen();
          }
        };

        this.propertyFullscreenCloseContainer = container as HTMLElement;

        // 👉 iOS: láta kontrollana liggja VED HLIDINA
        if (this.isIosFullscreenEnvironment) {
          const topLeftCorner = _map._controlCorners['topleft'] as HTMLElement;

          // Bæta wrapper-class á hornið sjálft (notað í CSS)
          topLeftCorner.classList.add('ios-fs-horizontal-controls');

          // Setjum "Loka glugga" FYRST, svo hann sé vinstra megin
          topLeftCorner.insertBefore(container, topLeftCorner.firstChild);
        }

        this.updatePropertyMapFullscreenCloseVisibility();
        return container;
      }
    });

    mapInstance.addControl(new FullscreenCloseControl());

    if (!this.propertyFullscreenCloseListenerAttached) {
      this.propertyFullscreenCloseListenerAttached = true;

      if (!this.isIosFullscreenEnvironment) {
        document.addEventListener('fullscreenchange', () => {
          this.updatePropertyMapFullscreenCloseVisibility();
        });
      }
    }
  }

  private updatePropertyMapFullscreenCloseVisibility() {
    if (!this.propertyFullscreenCloseContainer) return;

    const visible = this.isInFs();
    this.propertyFullscreenCloseContainer.style.display = visible ? 'block' : 'none';

    if (!visible) {
      this.sharedPopup.close();
      this.map.setZoom(15, { animate: false });
      setTimeout(() => {
        this.recenterToMain();
      }, 500);
    }
  }

  //#endregion

  //#region -- ClusterGroup

  // Fyrir utan init-kóðann (t.d. sem field í component)
  private propertyClusterOpenInstant = false;
  private propertyClusterOpenDelayed = false;
  private propertyClusterOpenTimer: any = null;

  // Hjálparfall: setur gildi með 1000ms delay
  private propertyClusterSetOpenWithDelay(isOpen: boolean): void {
    this.propertyClusterOpenInstant = isOpen;

    if (this.propertyClusterOpenTimer) {
      clearTimeout(this.propertyClusterOpenTimer);
    }

    this.propertyClusterOpenTimer = setTimeout(() => {
      this.propertyClusterOpenDelayed = this.propertyClusterOpenInstant;
      // hér gætirðu t.d. kallað á changeDetection eða whatever
    }, 1000);
  }

  //#endregion

  //#endregion

  //#region Map loader

  private propertyLoaderContainerRef: HTMLElement | null = null;
  private propertyLoaderCountRef: HTMLElement | null = null;

  private initPropertyLoaderControl(mapInstance: any) {
    const PropertyLoadingControl = this._leafletService.L.Control.extend({
      options: { position: 'bottomright' },

      onAdd: (_map: any) => {
        const container = this._leafletService.L.DomUtil.create('div', 'property-loading-control');
        container.style.display = 'none';

        container.innerHTML = `
        <div class="property-loading-inner">
         <span class="property-spinner"></span>
          <span>
            <strong>Hleð eignum:</strong> (<span class="property-loader-count">0</span>)
          </span>
        </div>
      `;

        this._leafletService.L.DomEvent.disableClickPropagation(container);
        this._leafletService.L.DomEvent.disableScrollPropagation(container);

        this.propertyLoaderContainerRef = container;
        this.propertyLoaderCountRef =
          container.querySelector('.property-loader-count');

        return container;
      }
    });

    mapInstance.addControl(new PropertyLoadingControl());
  }


  private setPropertyLoaderCount(count: number): void {
    if (!this.propertyLoaderCountRef) return;
    this.propertyLoaderCountRef.textContent = thousandSeparator(String(count));
  }

  private setPropertyLoaderVisibility(isVisible: boolean): void {
    if (!this.propertyLoaderContainerRef) return;
    this.propertyLoaderContainerRef.style.display = isVisible ? 'block' : 'none';
  }

  //#endregion


  //#region Rafrænt verðmat

  // Segir til um hvort verðmat byggi á fastanúmeri.
  // Ef false, þá var aðeins notast við staðsetningu.
  verdmatFastanumer: boolean = null;

  getVerdmat() {
    if (this._fasteign?.einflm) {
      this._fasteignService.estimatePropertyValue(this._fasteign.fastnum.toString()).subscribe(verdmat => {
        if (verdmat?.estimatedValueSqm != null) {
          this.verdmatFastanumer = true;
          this.verdmat = verdmat;
        } else {
          this._fasteignService.estimatePropertyValueLatLong({
            LatWgs84: this._fasteign.latWgs84,
            LongWgs84: this._fasteign.longWgs84
          }).subscribe(verdmat => {
            this.verdmatFastanumer = false;
            this.verdmat = verdmat;
          });
        }
      });
    } else {
      this._fasteignService.estimatePropertyValueLatLong({
        LatWgs84: this._fasteign.latWgs84,
        LongWgs84: this._fasteign.longWgs84
      }).subscribe(verdmat => {
        this.verdmatFastanumer = false;
        this.verdmat = verdmat;
      });
    }
  }

  formatVerdmatSqm(verdmatSqm: number): string {
    if (!verdmatSqm) return '';

    const flooredValue = Math.floor(verdmatSqm * 100) / 100; // Fjarlægir síðustu tvo aukastafina
    const newValue = Math.floor(flooredValue) * 100; // Skipta út síðustu tveimur fyrir 00
    return newValue.toFixed(0) + '0 kr./m²'; // Bætir við einu 0 í lokin
  }

  //#endregion

  //#region Verðmat fasteignasala

  verdmatBeidniSend = false;
  verdmatBeidniBeingSent = false;
  verdmatError = false;
  public formVerdmat: UntypedFormGroup;

  initFormVerdmat() {
    this.resetVerdmatForm();
    if (getCookie('accessToken') && !this.userDetails) {
      this._notandiService.fetchUserDetails().subscribe(user => {
        this.userDetails = user;
        this.initFormVerdmatData();
      });
    } else {
      this.initFormVerdmatData();
    }
  }

  initFormVerdmatData() {
    if (this.userDetails) {
      this.formVerdmat = this._fb.group({
        kennitala: new FormControl(this.userDetails?.kennitala),
        nafn: new FormControl(this.userDetails.nafn, [Validators.required]),
        simanumer: new FormControl(this.userDetails?.simanumer, [Validators.required]),
        netfang: new FormControl(this.userDetails?.netfang, [Validators.required, ValidateEmail]),
        skilabod: new FormControl("", [Validators.required])
      });
    } else {
      this.formVerdmat = this._fb.group({
        kennitala: new FormControl(""),
        nafn: new FormControl("", [Validators.required]),
        simanumer: new FormControl("", [Validators.required]),
        netfang: new FormControl("", [Validators.required, ValidateEmail]),
        skilabod: new FormControl("", [Validators.required])
      });
    }
    this.verdmatBeidniSend = false; // Ensure the form is shown again after reset
    this.verdmatBeidniBeingSent = false; // Reset beingSent to false

    try {
      this.eignarSamantekt().then(samantekt => {
        this.formVerdmat.get('skilabod').setValue("Ég óska eftir fríu verðmati í eftirfarandi fasteign => " + samantekt);
      });
    } catch { }
  }

  changeVerdmatForm() {
    this.verdmatError = false;
    this.verdmatBeidniSend = false;
    this.verdmatBeidniBeingSent = false;
  }

  // Þarf fyrir MBL takka.
  showFormValidationErrorsVerdmatBeidni() {
    try {
      Object.keys(this.formVerdmat.controls).forEach(field => {
        const control = this.formVerdmat.get(field);
        if (control) {
          control.markAsTouched();
          control.markAsDirty();
          control.updateValueAndValidity();
        }
      });
    } catch { }
  }

  public sendaBeidniUmVerdmat(): void {
    this.showFormValidationErrorsVerdmatBeidni();
    this.verdmatError = false;

    if (this.formVerdmat.valid) {
      this.verdmatBeidniBeingSent = true;
      const req: ExposedHafaSambandReq = {
        kennitala: this.formVerdmat.get("kennitala").value,
        nafn: this.formVerdmat.get("nafn").value,
        netfang: this.formVerdmat.get("netfang").value,
        simanumer: this.formVerdmat.get("simanumer").value,
        skilabod: this.formVerdmat.get("skilabod").value
      };
      this._hafaSambandService.beidniFrittVerdmat(req, false).subscribe(success => {
        this.verdmatBeidniBeingSent = false;
        if (success) {
          this.verdmatBeidniSend = true;
        } else {
          this.verdmatBeidniSend = false;
          this.verdmatError = true;
        }
      });
    }
  }

  private resetVerdmatForm(): void {
    if (this.formVerdmat) {
      this.formVerdmat.reset();
      Object.keys(this.formVerdmat.controls).forEach(key => {
        this.formVerdmat.get(key).setErrors(null);
        this.formVerdmat.get(key).markAsPristine();
        this.formVerdmat.get(key).markAsUntouched();
      });
    }
  }

  private async eignarSamantekt(): Promise<string> {
    for (let i = 0; i < 10; i++) {
      if (this._fasteign) {
        const heimilisfang = this._fasteign.heimilisfang || '';
        const fastanumer = this._fasteign.fastnum || '';
        const hæð = this.epilogExtractHeightNumber(this._fasteign.epilog);
        const hæðStr = hæð > 1 || this._fasteign.tegund === 'Fjölbýli' ? `, ${hæð}. hæð` : '';
        const tegundStr = this._fasteign.tegund ? `, ${this._fasteign.tegund}` : '';
        const fermetrarStr = this._fasteign.einflm ? ` (${this._fasteign.einflm.toString().replace('.', ',')} m²)` : '';
        const ibudStr = this._fasteign.tegund === 'Fjölbýli'
          ? `, Íbúð: ${this.epilogExtractApartmentIdentity(this._fasteign.epilog)}`
          : '';

        const stadsetning = [
          this.trim(this._fasteign.postNumer),
          this.trim(this._fasteign.sveitarfelag),
          this.trim(this._fasteign.svaedi)
        ].filter(Boolean).join(', ');

        return `Fastanúmer: ${fastanumer}. Heimilisfang: ${heimilisfang}${hæðStr}${tegundStr}${fermetrarStr}${ibudStr}. Staðsetning: ${stadsetning}.`;
      }
      await new Promise(r => setTimeout(r, 500));
    }
    return ''; // ef fasteign kom ekki innan 5 sek.
  }

  // Þegar notandinn velur hlekk efst uppi að fá frítt verðmat frá löggiltum fasteignasala.
  clickVerdmatsbeidniLink() {
    if (this.pageInject) {
      const url = `/kaupsagan/${this.fastnum}/verdmatsbeidni`;
      window.open(url, '_blank');
    } else {
      this.scrollToElement('verdmatsbeidni');
    }
  }

  //#endregion

  //#region Helper functions

  openPropertyImageGallery(images: any, startIndex: number = 0): void {
    const baseUrl = this._environment.localApi + 'FasteignKaupskra/saekjaMynd/Fs/';

    const items = (images || []).map((img: any) => ({
      src: baseUrl + img.azureName,
      type: 'image'
    }));

    Fancybox.show(items, {
      startIndex: startIndex,
      // hér geturðu sett aðrar stillingar ef þú vilt, t.d.:
      // Thumbs: { autoStart: true },
      // infinite: false,
    });
  }

  formatToMillionKr(value: number): string {
    if (value == null || !Number.isFinite(value)) return "";

    const millions = value / 1_000_000;
    const rounded = Math.round(millions * 10) / 10; // 1 aukastafur, rétt námundun

    // Reynum fyrst með 'is-IS' ef hún er til staðar
    try {
      const supportsIS =
        typeof Intl !== "undefined" &&
        typeof Intl.NumberFormat === "function" &&
        Intl.NumberFormat.supportedLocalesOf?.(["is-IS"]).length > 0;

      if (supportsIS) {
        // min 0 og max 1 → sleppir ,0 ef heiltala
        return `${new Intl.NumberFormat("is-IS", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
          useGrouping: true,
        }).format(rounded)} m.kr.`;
      }
    } catch {
      // fellum í handvirka sniðun hér fyrir neðan
    }

    // Fallback: tryggjum „,“ sem tugabrot og „.“ sem þúsundaaðskiljara
    const fixed = rounded.toFixed(1); // t.d. "107.0" eða "106.9"
    const [intPart, decPart = "0"] = fixed.split(".");
    const intWithDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimal = decPart === "0" ? "" : `,${decPart}`;
    return `${intWithDots}${decimal} m.kr.`;
  }

  metersToPixels(meters: number, lat: number, zoom: number): number {
    const metersPerPixel = 40075016.686 * Math.abs(Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom + 8);
    return meters / metersPerPixel;
  }

  async reverseGeocode(lat: number, lon: number): Promise<{ street: string | null, postcode: string | null, town: string | null }> {
    const url = new URL('https://nominatim.openstreetmap.org/reverse');
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('lat', String(lat));
    url.searchParams.set('lon', String(lon));
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('zoom', '18');
    url.searchParams.set('email', 'e-fasteignir@e-fasteignir.is'); // settu þitt netfang

    const res = await fetch(url.toString(), {
      headers: { 'User-Agent': 'lsr-leaflet-demo/1.0 (e-fasteignir@e-fasteignir.is)' }
    });

    if (!res.ok) {
      return { street: null, postcode: null, town: null };
    }

    const data = await res.json();
    const a = data?.address;
    if (!a) {
      return { street: null, postcode: null, town: null };
    }

    // street getur komið frá mörgum "tags"
    const street =
      a.road ||
      a.pedestrian ||
      a.footway ||
      a.cycleway ||
      a.path ||
      a.residential ||
      a.neighbourhood ||
      null;

    const hn = a.house_number;
    const streetLabel = street ? (hn ? `${street} ${hn}` : street) : null;

    const town =
      a.city ||
      a.town ||
      a.village ||
      a.hamlet ||
      a.suburb ||
      a.city_district ||
      null;

    const postcode = a.postcode ?? null;

    return { street: streetLabel, postcode, town };
  }

  opnaAuglysingu(index: number, auglysing: ListFastinnFasteignAdsWithImagesRes): void {
    let valinAuglysing = this.auglysingasaga.find(x => x.fastinnFasteignId == auglysing.fastinnFasteignId);

    if (!valinAuglysing?.images?.length) {
      this._fasteignService.listAuglysingasagaMyndir(auglysing.fastinnFasteignId).subscribe(imagesRes => {
        this.auglysingasaga[this.auglysingasaga.findIndex(x => x.fastinnFasteignId == auglysing.fastinnFasteignId)].images = imagesRes;
      });
    }
  }

  private heightIntervalStarted = false;
  private lastSentHeight = 0;
  private fastIntervalId: any;

  sendHeightToParent(): void {
    if (this._isBrowser && !this.heightIntervalStarted) {
      this.heightIntervalStarted = true;

      const checkHeight = () => {
        const el = document.querySelector('.kaupsagan-container') as HTMLElement | null;
        const newHeight = el ? el.scrollHeight : 400;

        if (newHeight !== this.lastSentHeight) {
          window.parent.postMessage({ type: 'iframeHeight', height: newHeight }, '*');
          this.lastSentHeight = newHeight;
        }
      };

      // 👉 Senda strax einu sinni
      checkHeight();

      // Fyrstu 10 sek → 50ms interval
      this.fastIntervalId = setInterval(checkHeight, 50);

      // Eftir 10 sek → skipta í 1000ms
      setTimeout(() => {
        clearInterval(this.fastIntervalId);
        setInterval(checkHeight, 1000);
      }, 10_000);
    }
  }

  // Makes sure that if another property is selected on the page the page will refresh accordingly.
  private coupdateOnFasteignClickCurrentPageCount = 0;
  updateOnFasteignClickCurrentPage() {
    if (this._isBrowser) {
      this._activatedRoute.params.subscribe(val => {
        this.coupdateOnFasteignClickCurrentPageCount++;
        if (this.coupdateOnFasteignClickCurrentPageCount >= 2) {
          this.reloadComponent();
        }
      });
    }
  }

  reloadComponent() {
    const currentUrl = this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }

  formatVerd(numberString: string | null) {
    return formatPriceString(numberString.toString());
  }

  thousandSeparator(numberString: string) {
    return thousandSeparator(numberString.toString());
  }

  getFermetraverdKaupsamningur(fasteign): string {
    try {
      return thousandSeparator((Math.ceil(fasteign.kaupverd * 1000 / fasteign.einflm).toString()));
    } catch {
      return "";
    }
  }

  getFermetraverdFasteignamatRikisins(fasteign): string {
    try {
      return thousandSeparator((Math.ceil(fasteign.fasteignamatGildandi * 1000 / fasteign.einflm).toString()));
    } catch {
      return "";
    }
  }

  getFermetraverdBrunabotamat(fasteign): string {
    try {
      return thousandSeparator((Math.ceil(fasteign.brunabotamatGildandi * 1000 / fasteign.einflm).toString()));
    } catch {
      return "";
    }
  }

  epilogExtractHeightNumber(epilogNumber: string) {
    return epilogExtractHeightNumber(epilogNumber);
  }

  epilogExtractApartmentIdentity(epilogNumber: string) {
    return epilogExtractApartmentIdentity(epilogNumber);
  }

  encodeURIComponent(v) {
    return encodeURIComponent(v);
  }

  scrollToElement(elementId: string): void {
    const target = document.getElementById(elementId);
    if (!target) return;

    const startY = window.scrollY;
    const targetY = target.getBoundingClientRect().top + window.scrollY;
    const duration = 250; // milliseconds
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1); // [0, 1]
      const easeInOut = progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;

      const scrollY = startY + (targetY - startY) * easeInOut;
      window.scrollTo(0, scrollY);

      if (elapsed < duration) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  trim(s) {
    try {
      return s.toString().trim();
    } catch {
      return s;
    }
  }

  ensureLeafletPopupButtonStyles() {
    const styleId = 'lsr-leaflet-btn-styles';
    if (document.getElementById(styleId)) return;

    const css = `
      .leaflet-popup-content .orange-btn {
        display:inline-flex; 
        align-items:center; 
        justify-content:center;
        padding: 10px 14px;               
        border-radius: 4px;
        font-size: 15px !important;       
        font-weight: 400 !important;      
        font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
        color:#fff; 
        text-decoration:none;
        background:#e06902;         
        box-shadow: inset 0 -2px rgba(0,0,0,.15);
        transition: filter .15s ease, transform .02s ease;
        line-height:1.2;
        white-space:nowrap;
      }
      .leaflet-popup-content .orange-btn:hover { filter: brightness(0.95); }
      .leaflet-popup-content .orange-btn:active { transform: translateY(1px); }

      .leaflet-popup-content .orange-btn--sm  { font-size: 0.95rem; }
      .leaflet-popup-content .orange-btn--md  { font-size: 1rem;   }
      .leaflet-popup-content .orange-btn--lg  { font-size: 1.05rem;}
      .leaflet-popup-content .orange-btn--full{ width:100%; }
      .leaflet-popup-content .orange-btn--outline {
        background:#fff; color:#e06902; border:1px solid #e06902; box-shadow:none;
      }

      .leaflet-popup-content .blue-btn {
        display:inline-flex; 
        align-items:center; 
        justify-content:center;
        padding: 10px 14px;               
        border-radius: 4px;
        font-size: 15px !important;       
        font-weight: 400 !important;      
        font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
        color:#fff; 
        text-decoration:none;
        background:#082a54;         
        box-shadow: inset 0 -2px rgba(0,0,0,.15);
        transition: filter .15s ease, transform .02s ease;
        line-height:1.2;
        white-space:nowrap;
      }
      .leaflet-popup-content .blue-btn:hover { filter: brightness(0.95); }
      .leaflet-popup-content .blue-btn:active { transform: translateY(1px); }

      .leaflet-popup-content .blue-btn--sm  { font-size: 0.95rem; }
      .leaflet-popup-content .blue-btn--md  { font-size: 1rem;   }
      .leaflet-popup-content .blue-btn--lg  { font-size: 1.05rem;}
      .leaflet-popup-content .blue-btn--full{ width:100%; }
      .leaflet-popup-content .blue-btn--outline {
        background:#fff; color:#082a54; border:1px solid #082a54; box-shadow:none;
      }
    `;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = css;
    document.head.appendChild(style);
  }

  isWithinOneYear(thinglystDags: string | Date): boolean {
    const today = new Date();

    // Notum bara new Date() ef þarf
    const thinglystDate = thinglystDags instanceof Date
      ? thinglystDags
      : new Date(thinglystDags);

    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    return thinglystDate >= oneYearAgo && thinglystDate <= today;
  }

  //#endregion

}
